// src/pages/Settings.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import FooterNav from "../components/FooterNav";
import LogoutModal from "../components/LogoutModal";
import RecoveryPhraseSetup from "../components/RecoveryPhraseSetup";
import { useLocation, useNavigate } from "react-router-dom";
import { applyDisplayPreferences, applyThemeClass } from "../utils/theme";
import {
    AtSign,
    Bell,
    BellRing,
    Clock3,
    EyeOff,
    Heart,
    ImagePlus,
    KeyRound,
    Laptop,
    LifeBuoy,
    LockKeyhole,
    LogOut,
    Mail,
    MapPin,
    MessageCircle,
    Palette,
    RefreshCw,
    RotateCcw,
    ScanQrCode,
    Search,
    ShieldAlert,
    ShieldOff,
    Smartphone,
    Tablet,
    Trash2,
    Users,
    Vault,
    UserRound,
    Volume2,
    X,
} from "lucide-react";
import {
    classNames,
    ConfirmDialog,
    ConfirmState,
    DirtySettingsBar,
    EmptySearchState,
    PasswordChangeDialog,
    PasswordFormState,
    Section,
    SettingRow,
    SettingsSkeleton,
    SettingsToast,
    Toggle,
} from "../features/settings/SettingsParts";

// API imports
import {
    AccountSession,
    getProfile,
    checkUsername as checkUsernameApi,
    deleteAccount,
    fetchAccountSessions,
    logoutAllDevices,
    revokeAccountSession,
    updateProfile,
    toggleKillSwitch,
    changePassword,
    setRecoveryPhrase,
} from "../api/profile";
import { generateRecoveryPhrase } from "../api/auth";
import { requestBrowserNotificationPermission } from "../utils/activeNotifications";

const API_BASE: string = (process.env.REACT_APP_API_BASE || "http://localhost:5000").replace(/\/$/, "");
const USERNAME_MAX_LENGTH = 30;
const PUBLIC_AUTH_NOTICE_KEY = "blackspace.publicAuthNotice";
const AVATAR_SOFT_RADIUS = 12;

/** ------------------------------
 * Helpers (live API wiring + utilities)
 * -------------------------------*/

async function saveSettings(payload: any) {
    // updateProfile throws on non 2xx and returns { success, user, ... }
    return updateProfile(payload);
}

async function checkUsernameAvailable(username: string): Promise<boolean> {
    const res = await checkUsernameApi(username);
    return Boolean((res as any)?.available ?? res);
}

function useDebounced<T>(value: T, delay = 500) {
    const [v, setV] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setV(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return v;
}

function clearLocalSession(clearLocalState = false) {
    const preservedTheme = localStorage.getItem("theme");
    const preservedFontSize = localStorage.getItem("fontSize");
    const preservedGlow = localStorage.getItem("glow");
    const preserved: Record<string, string> = {};

    if (!clearLocalState) {
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (!key) continue;
            if (key.startsWith("iconOrder_") || key.startsWith("preBlock_") || key === "incognitoChatIds") {
                const value = localStorage.getItem(key);
                if (value !== null) preserved[key] = value;
            }
        }
    }

    sessionStorage.clear();

    if (clearLocalState) {
        localStorage.clear();
    } else {
        ["token", "authToken", "user", "userData", "globalIncognito"].forEach((key) => localStorage.removeItem(key));
    }

    if (preservedTheme) localStorage.setItem("theme", preservedTheme);
    if (preservedFontSize) localStorage.setItem("fontSize", preservedFontSize);
    if (preservedGlow) localStorage.setItem("glow", preservedGlow);
    Object.entries(preserved).forEach(([key, value]) => localStorage.setItem(key, value));
}

function formatSessionLoginTime(value?: string | null) {
    if (!value) return "--:--";
    const time = new Date(value).getTime();
    if (!Number.isFinite(time)) return "--:--";
    return new Intl.DateTimeFormat(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    }).format(new Date(time));
}

function sessionDeviceIcon(session: AccountSession) {
    if (session.deviceType === "mobile") return <Smartphone size={16} />;
    if (session.deviceType === "tablet") return <Tablet size={16} />;
    return <Laptop size={16} />;
}

/** ------------------------------
 * Types & Initials
 * -------------------------------*/

type LastSeenVisibility = "Everyone" | "Contacts" | "Nobody";
type AudienceVisibility = "Everyone" | "Friends" | "Nobody";
type MessageReach = "Anyone" | "Friends" | "No One";
type VaultDefaultView = "both" | "notes" | "folders";
type VaultSharePermission = "view" | "edit";
type VaultExportFormat = "json" | "markdown" | "pdf";
type SpacesDefaultVisibility = "public" | "invite" | "private";
type SpacesPresenceVisibility = "visible" | "members" | "hidden";
type AppearanceTheme = "Auto" | "AMOLED" | "Noir" | "Dark" | "Light" | "Archive" | "Solarized" | "Signal Blue" | "Abyss" | "Infrared" | "Aurora" | "Deep Field";
type LanguageCode = "EN" | "HI" | "FR" | "IT" | "RU" | "ZA" | "AR";

type FormState = {
    username: string;
    displayName: string;
    bio: string;
    avatarUrl: string;
    email: string;
    avatarFile?: File | null;
    // Privacy
    accountVisibility: "public" | "private";
    incognitoMode: boolean;
    showOnline: boolean;
    lastSeen: LastSeenVisibility;
    readReceipts: boolean;
    whoCanMessage: MessageReach;
    whoCanInviteGroups: MessageReach;
    searchVisibility: AudienceVisibility;
    activityVisibility: AudienceVisibility;
    presenceVisibility: AudienceVisibility;
    // Security
    twoFactorAuth: boolean;
    loginAlerts: boolean;
    // Notifications
    pushNotifications: boolean;
    emailNotifications: boolean;
    soundEffects: boolean;
    notifyMessages: boolean;
    notifySpaces: boolean;
    notifyVault: boolean;
    notifyMentions: boolean;
    notifyReactions: boolean;
    notifySecurity: boolean;
    // Appearance
    theme: AppearanceTheme;
    fontSize: "Small" | "Medium" | "Large";
    glow: boolean;
    reduceMotion: boolean;
    highContrast: boolean;
    language: LanguageCode;
    visibleThemes: AppearanceTheme[];
    visibleLanguages: LanguageCode[];
    // Vault
    vaultDefaultView: VaultDefaultView;
    vaultAutosave: boolean;
    vaultDefaultSharePermission: VaultSharePermission;
    vaultRecycleRetentionDays: number;
    vaultExportFormat: VaultExportFormat;
    // Spaces
    spacesDefaultVisibility: SpacesDefaultVisibility;
    spacesEventReminders: boolean;
    spacesJoinRequestAlerts: boolean;
    spacesArchiveAlerts: boolean;
    spacesPresenceVisibility: SpacesPresenceVisibility;
    // Experimental
    aiSuggestions: boolean;
    betaFeatures: boolean;
};

type RealmState = {
    role?: string;
    isPresence?: boolean;
    canAccessSentinel?: boolean;
    canBypassSecurity?: boolean;
    canUseAnonymous?: boolean;
    profileMessage?: string;
} | null;

const initialForm: FormState = {
    username: "",
    displayName: "",
    bio: ``,
    avatarUrl:
        "",
    email: "",
    // Privacy
    accountVisibility: "public",
    incognitoMode: false,
    showOnline: true,
    lastSeen: "Everyone",
    readReceipts: true,
    whoCanMessage: "Anyone",
    whoCanInviteGroups: "Anyone",
    searchVisibility: "Everyone",
    activityVisibility: "Friends",
    presenceVisibility: "Friends",
    // Security
    twoFactorAuth: false,
    loginAlerts: true,
    // Notifications
    pushNotifications: true,
    emailNotifications: false,
    soundEffects: true,
    notifyMessages: true,
    notifySpaces: true,
    notifyVault: true,
    notifyMentions: true,
    notifyReactions: true,
    notifySecurity: true,
    // Appearance
    theme: "Light",
    fontSize: "Medium",
    glow: true,
    reduceMotion: false,
    highContrast: false,
    language: "EN",
    visibleThemes: ["AMOLED", "Noir", "Dark", "Signal Blue", "Abyss", "Deep Field"],
    visibleLanguages: ["EN", "HI", "FR", "IT", "RU", "ZA", "AR"],
    // Vault
    vaultDefaultView: "both",
    vaultAutosave: true,
    vaultDefaultSharePermission: "view",
    vaultRecycleRetentionDays: 30,
    vaultExportFormat: "json",
    // Spaces
    spacesDefaultVisibility: "public",
    spacesEventReminders: true,
    spacesJoinRequestAlerts: true,
    spacesArchiveAlerts: true,
    spacesPresenceVisibility: "members",
    // Experimental
    aiSuggestions: false,
    betaFeatures: false,
};

const SETTINGS_SNAPSHOT_KEY = "blackspace.settings.snapshot.v1";
const SETTINGS_SNAPSHOT_TTL_MS = 15 * 60 * 1000;

type SettingsSnapshot = {
    form: FormState;
    initial: FormState;
    realm: RealmState;
    searchQuery: string;
    scrollY: number;
    savedAt: number;
};

function readSettingsSnapshot(): SettingsSnapshot | null {
    try {
        const raw = sessionStorage.getItem(SETTINGS_SNAPSHOT_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as SettingsSnapshot;
        if (!parsed.form || !parsed.initial) return null;
        if (Date.now() - Number(parsed.savedAt || 0) > SETTINGS_SNAPSHOT_TTL_MS) {
            sessionStorage.removeItem(SETTINGS_SNAPSHOT_KEY);
            return null;
        }
        parsed.form.whoCanMessage = normalizeMessageReach(parsed.form.whoCanMessage);
        parsed.form.whoCanInviteGroups = normalizeMessageReach(parsed.form.whoCanInviteGroups);
        parsed.initial.whoCanMessage = normalizeMessageReach(parsed.initial.whoCanMessage);
        parsed.initial.whoCanInviteGroups = normalizeMessageReach(parsed.initial.whoCanInviteGroups);
        return parsed;
    } catch {
        return null;
    }
}

function writeSettingsSnapshot(snapshot: SettingsSnapshot) {
    try {
        sessionStorage.setItem(SETTINGS_SNAPSHOT_KEY, JSON.stringify(snapshot));
    } catch {
        // Settings can always fall back to the profile endpoint.
    }
}

function normalizeMessageReach(value: unknown): MessageReach {
    if (value === "Friends" || value === "No One" || value === "Anyone") return value;
    return "Anyone";
}

function normalizeAudience(value: unknown, fallback: AudienceVisibility = "Everyone"): AudienceVisibility {
    if (value === "Everyone" || value === "Friends" || value === "Nobody") return value;
    return fallback;
}

function normalizeVaultDefaultView(value: unknown): VaultDefaultView {
    if (value === "notes" || value === "folders" || value === "both") return value;
    return "both";
}

function normalizeVaultSharePermission(value: unknown): VaultSharePermission {
    if (value === "edit" || value === "view") return value;
    return "view";
}

function normalizeVaultExportFormat(value: unknown): VaultExportFormat {
    if (value === "markdown" || value === "pdf" || value === "json") return value;
    return "json";
}

function normalizeSpacesDefaultVisibility(value: unknown): SpacesDefaultVisibility {
    if (value === "invite" || value === "private" || value === "public") return value;
    return "public";
}

function normalizeSpacesPresenceVisibility(value: unknown): SpacesPresenceVisibility {
    if (value === "visible" || value === "members" || value === "hidden") return value;
    return "members";
}

const THEME_OPTIONS: Array<{
    name: FormState["theme"];
    bestFor: string;
    swatches: string[];
}> = [
    { name: "Auto", bestFor: "System", swatches: ["#000000", "#f8fafc", "#22d3ee"] },
    { name: "AMOLED", bestFor: "Night", swatches: ["#000000", "#0a0a0a", "#00e5ff"] },
    { name: "Noir", bestFor: "Focus", swatches: ["#070708", "#151518", "#d8d1c1"] },
    { name: "Dark", bestFor: "Daily", swatches: ["#000000", "#171717", "#22d3ee"] },
    { name: "Light", bestFor: "Daylight", swatches: ["#f8fafc", "#ffffff", "#0891b2"] },
    { name: "Archive", bestFor: "Long reads", swatches: ["#f4efe2", "#fffaf0", "#7c3f18"] },
    { name: "Solarized", bestFor: "Writing", swatches: ["#fdf6e3", "#eee8d5", "#268bd2"] },
    { name: "Signal Blue", bestFor: "Messages", swatches: ["#0a1628", "#0f2340", "#0096ff"] },
    { name: "Abyss", bestFor: "Calm", swatches: ["#061214", "#0d2529", "#2dd4bf"] },
    { name: "Infrared", bestFor: "Sentinel", swatches: ["#100607", "#220d13", "#fb7185"] },
    { name: "Aurora", bestFor: "Creative", swatches: ["#07110f", "#102221", "#67e8f9"] },
    { name: "Deep Field", bestFor: "Spaces", swatches: ["#0d0a1a", "#16122e", "#c084fc"] },
];

const THEME_GROUPS: Array<{ title: string; description: string; themes: FormState["theme"][] }> = [
    { title: "Core", description: "Daily Blackspace surfaces.", themes: ["Auto", "AMOLED", "Noir", "Dark", "Light"] },
    { title: "Reading", description: "Long notes and calm text.", themes: ["Archive", "Solarized"] },
    { title: "Mood", description: "Spaces, signals, and night modes.", themes: ["Signal Blue", "Abyss", "Infrared", "Aurora", "Deep Field"] },
];

const LANGUAGE_OPTIONS: ReadonlyArray<{ code: LanguageCode; label: string }> = [
    { code: "EN", label: "English" },
    { code: "HI", label: "Hindi" },
    { code: "FR", label: "French" },
    { code: "IT", label: "Italian" },
    { code: "RU", label: "Russian" },
    { code: "ZA", label: "South Africa" },
    { code: "AR", label: "Arabic" },
] as const;

const DEFAULT_VISIBLE_THEMES: AppearanceTheme[] = ["AMOLED", "Noir", "Dark", "Signal Blue", "Abyss", "Deep Field"];
const DEFAULT_VISIBLE_LANGUAGES: LanguageCode[] = ["EN", "HI", "FR", "IT", "RU", "ZA", "AR"];

function sameStringList(a: readonly string[] = [], b: readonly string[] = []) {
    if (a.length !== b.length) return false;
    return a.every((item, index) => item === b[index]);
}

function normalizeThemeName(theme: string): FormState["theme"] {
    if (theme === "Zima Blue") return "Signal Blue";
    if (theme === "Sea") return "Abyss";
    if (theme === "Space") return "Deep Field";
    if (THEME_OPTIONS.some((option) => option.name === theme)) return theme as FormState["theme"];
    return "AMOLED";
}

function buildAppearancePayload(state: FormState) {
    const theme = normalizeThemeName(String(state.theme || "AMOLED"));
    const language = normalizeLanguageCode(state.language);
    const visibleThemes = normalizeThemeList(state.visibleThemes, DEFAULT_VISIBLE_THEMES);
    const visibleLanguages = normalizeLanguageList(state.visibleLanguages, DEFAULT_VISIBLE_LANGUAGES);

    return {
        theme,
        fontSize: (state.fontSize as FormState["fontSize"]) ?? "Medium",
        glow: Boolean(state.glow ?? true),
        reduceMotion: Boolean(state.reduceMotion ?? false),
        highContrast: Boolean(state.highContrast ?? false),
        language,
        visibleThemes: visibleThemes.includes(theme) ? visibleThemes : uniqueList([theme, ...visibleThemes]).slice(0, THEME_OPTIONS.length),
        visibleLanguages: visibleLanguages.includes(language) ? visibleLanguages : uniqueList([language, ...visibleLanguages]).slice(0, LANGUAGE_OPTIONS.length),
    };
}

function appearanceSignature(state: FormState) {
    return JSON.stringify(buildAppearancePayload(state));
}

function writeAppearanceStorage(appearance: ReturnType<typeof buildAppearancePayload>) {
    localStorage.setItem("theme", appearance.theme);
    localStorage.setItem("fontSize", appearance.fontSize);
    localStorage.setItem("glow", appearance.glow ? "true" : "false");
    localStorage.setItem("reduceMotion", appearance.reduceMotion ? "true" : "false");
    localStorage.setItem("highContrast", appearance.highContrast ? "true" : "false");
    localStorage.setItem("visibleThemes", JSON.stringify(appearance.visibleThemes || []));
    localStorage.setItem("visibleLanguages", JSON.stringify(appearance.visibleLanguages || []));
    localStorage.setItem("blackspace.settings.appearance", JSON.stringify(appearance || {}));
    applyThemeClass(appearance.theme);
    applyDisplayPreferences(
        appearance.fontSize,
        appearance.glow,
        appearance.reduceMotion,
        appearance.highContrast
    );
}

function productChoiceLabel(value: string) {
    if (value === "Friends") return "Followers";
    if (value === "No One") return "No one";
    return value;
}

function normalizePersistedAppearance(settings: any, fallback: ReturnType<typeof buildAppearancePayload>) {
    const source = settings || {};
    return {
        theme: normalizeThemeName(String(source.theme ?? fallback.theme)),
        fontSize: (["Small", "Medium", "Large"].includes(source.fontSize) ? source.fontSize : fallback.fontSize) as FormState["fontSize"],
        glow: typeof source.glow === "boolean" ? source.glow : fallback.glow,
        reduceMotion: typeof source.reduceMotion === "boolean" ? source.reduceMotion : fallback.reduceMotion,
        highContrast: typeof source.highContrast === "boolean" ? source.highContrast : fallback.highContrast,
        language: normalizeLanguageCode(source.language ?? fallback.language),
        visibleThemes: normalizeThemeList(source.visibleThemes ?? fallback.visibleThemes, fallback.visibleThemes),
        visibleLanguages: normalizeLanguageList(source.visibleLanguages ?? fallback.visibleLanguages, fallback.visibleLanguages),
    };
}

function appearanceMatches(a: ReturnType<typeof buildAppearancePayload>, b: ReturnType<typeof buildAppearancePayload>) {
    return (
        a.theme === b.theme &&
        a.fontSize === b.fontSize &&
        a.glow === b.glow &&
        a.reduceMotion === b.reduceMotion &&
        a.highContrast === b.highContrast &&
        a.language === b.language &&
        sameStringList(a.visibleThemes, b.visibleThemes) &&
        sameStringList(a.visibleLanguages, b.visibleLanguages)
    );
}

const DefaultSilhouette = () => (
    <svg
        viewBox="0 0 100 100"
        className="w-full h-full text-neutral-700"
        fill="currentColor"
    >
        <circle cx="50" cy="35" r="18" />
        <path d="M20 85c0-18 14-28 30-28s30 10 30 28v5H20v-5z" />
    </svg>
);


/** ------------------------------
 * Component
 * -------------------------------*/

export default function Settings() {
    const restoredSettingsRef = useRef<SettingsSnapshot | null>(readSettingsSnapshot());
    const restoredSettings = restoredSettingsRef.current;
    const [form, setForm] = useState<FormState>(() => hydrateFormState(restoredSettings?.form || initialForm));
    const [initial, setInitial] = useState<FormState>(() => hydrateFormState(restoredSettings?.initial || initialForm));
    const formRef = useRef<FormState>(form);
    const initialRef = useRef<FormState>(initial);
    const [realm, setRealm] = useState<RealmState>(() => restoredSettings?.realm || null);
    const logoutInProgressRef = useRef(false);

    const [saving, setSaving] = useState(false);
    const [saveMsg, setSaveMsg] = useState<
        null | { kind: "ok" | "err"; text: string }
    >(null);
    const [loading, setLoading] = useState(() => !restoredSettings);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [killSwitchLoading, setKillSwitchLoading] = useState(false);
    const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
    const [confirmBusy, setConfirmBusy] = useState(false);
    const [confirmText, setConfirmText] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const confirmPasswordRef = useRef("");
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [passwordForm, setPasswordForm] = useState<PasswordFormState>({
        currentPassword: "",
        recoveryPhrase: "",
        newPassword: "",
        confirmPassword: "",
    });
    const [passwordSaving, setPasswordSaving] = useState(false);
    const [passwordError, setPasswordError] = useState("");
    const [accountSessions, setAccountSessions] = useState<AccountSession[]>([]);
    const [sessionsLoading, setSessionsLoading] = useState(false);
    const [sessionsError, setSessionsError] = useState("");
    const [revokingSessionId, setRevokingSessionId] = useState<string | null>(null);
    const [appearanceModal, setAppearanceModal] = useState<"themes" | "languages" | null>(null);
    const [twoFactorModalOpen, setTwoFactorModalOpen] = useState(false);
    const [twoFactorSaving, setTwoFactorSaving] = useState(false);
    const [twoFactorError, setTwoFactorError] = useState("");
    const [recoveryModalOpen, setRecoveryModalOpen] = useState(false);
    const [recoveryPhraseStatus, setRecoveryPhraseStatus] = useState<"active" | "used" | "missing">("missing");
    const [recoveryCurrentPassword, setRecoveryCurrentPassword] = useState("");
    const [recoveryPreviousPhrase, setRecoveryPreviousPhrase] = useState("");
    const [recoveryGeneratedPhrase, setRecoveryGeneratedPhrase] = useState("");
    const [recoveryPhraseConfirmed, setRecoveryPhraseConfirmed] = useState(false);
    const [recoveryGenerating, setRecoveryGenerating] = useState(false);
    const [recoverySaving, setRecoverySaving] = useState(false);
    const [recoveryError, setRecoveryError] = useState("");
    const recoveryGenerationRef = useRef(0);
    const toastTimerRef = useRef<number | null>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const settingsTarget = location.hash.slice(1);
    const targetSectionId = settingsTarget.startsWith("settings-appearance-")
        ? "settings-appearance"
        : settingsTarget;
    const sectionExpandSignal = `${location.key}:${settingsTarget}`;

    const [searchQuery, setSearchQuery] = useState(() => restoredSettings?.searchQuery || "");
    const [settingsSearchOpen, setSettingsSearchOpen] = useState(() => Boolean(restoredSettings?.searchQuery));
    const settingsSearchRef = useRef<HTMLDivElement | null>(null);
    const settingsSearchInputRef = useRef<HTMLInputElement | null>(null);

    // Username validation + availability
    const [usernameTouched, setUsernameTouched] = useState(false);
    const debouncedUsername = useDebounced(form.username, 500);
    const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(
        null
    );
    const [checkingUser, setCheckingUser] = useState(false);

    useEffect(() => {
        formRef.current = form;
    }, [form]);

    useEffect(() => {
        initialRef.current = initial;
    }, [initial]);

    useEffect(() => {
        if (!settingsSearchOpen) return;
        const timer = window.setTimeout(() => settingsSearchInputRef.current?.focus(), 40);
        return () => window.clearTimeout(timer);
    }, [settingsSearchOpen]);

    useEffect(() => {
        if (!settingsSearchOpen) return;
        const onPointerDown = (event: PointerEvent) => {
            const target = event.target as Node | null;
            if (target && settingsSearchRef.current?.contains(target)) return;
            setSettingsSearchOpen(false);
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") setSettingsSearchOpen(false);
        };
        document.addEventListener("pointerdown", onPointerDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("pointerdown", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [settingsSearchOpen]);

    // Bio counter
    const bioLimit = 160;

    // Avatar upload progress
    const [, setUploadProgress] = useState(0);

    // ✅ ADDED: Filter settings sections based on search
    const [filteredSections, setFilteredSections] = useState<string[]>([]);
    const isPresence = Boolean(realm?.isPresence);
    const canAccessSentinel = Boolean(realm?.isPresence && realm?.canAccessSentinel);

    const showNotice = useCallback((kind: "ok" | "err", text: string, duration = 3000) => {
        if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
        setSaveMsg({ kind, text });
        toastTimerRef.current = window.setTimeout(() => setSaveMsg(null), duration);
    }, []);

    const togglePushNotifications = useCallback(async () => {
        const turningOn = !form.pushNotifications;
        if (!turningOn) {
            setForm((prev) => ({ ...prev, pushNotifications: false }));
            return;
        }

        const permission = await requestBrowserNotificationPermission();
        if (permission === "granted") {
            setForm((prev) => ({ ...prev, pushNotifications: true }));
            showNotice("ok", "Browser notifications enabled.");
            return;
        }

        setForm((prev) => ({ ...prev, pushNotifications: false }));
        showNotice(
            "err",
            permission === "unsupported"
                ? "This browser does not support active push notifications."
                : "Browser notifications are blocked. Enable them in your browser to use Push."
        );
    }, [form.pushNotifications, showNotice]);

    const loadAccountSessions = useCallback(async (quiet = true) => {
        if (isPresence) {
            setAccountSessions([]);
            setSessionsError("");
            setSessionsLoading(false);
            return;
        }
        setSessionsLoading(true);
        setSessionsError("");
        try {
            const res = await fetchAccountSessions();
            setAccountSessions(Array.isArray(res.sessions) ? res.sessions : []);
            if (!quiet) showNotice("ok", "Sessions refreshed.");
        } catch (error: any) {
            const message = error?.message || "Could not load logged in IPs.";
            setSessionsError(message);
            if (!quiet) showNotice("err", message);
        } finally {
            setSessionsLoading(false);
        }
    }, [isPresence, showNotice]);

    const openConfirm = (next: ConfirmState) => {
        setConfirmText("");
        setConfirmPassword("");
        confirmPasswordRef.current = "";
        setConfirmState(next);
    };

    const closeConfirm = () => {
        if (confirmBusy) return;
        setConfirmState(null);
        setConfirmText("");
        setConfirmPassword("");
        confirmPasswordRef.current = "";
    };

    const runConfirm = async () => {
        if (!confirmState) return;
        if (confirmState.requireText && confirmText !== confirmState.requireText) return;
        if (confirmState.requirePassword && !confirmPasswordRef.current) return;
        setConfirmBusy(true);
        try {
            await confirmState.onConfirm();
            setConfirmState(null);
            setConfirmText("");
            setConfirmPassword("");
            confirmPasswordRef.current = "";
        } catch (error: any) {
            showNotice("err", error?.message || "Action failed.");
        } finally {
            setConfirmBusy(false);
        }
    };

    useEffect(() => {
        return () => {
            if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
        };
    }, []);

    useEffect(() => {
        if (loading || isPresence) return;
        loadAccountSessions(true);
    }, [isPresence, loadAccountSessions, loading]);

    useEffect(() => {
        if (loading || isPresence) return;
        const timer = window.setInterval(() => {
            loadAccountSessions(true);
        }, 30000);
        return () => window.clearInterval(timer);
    }, [isPresence, loadAccountSessions, loading]);

    useEffect(() => {
        const snapshotForm: FormState = { ...form, avatarFile: null };
        const snapshotInitial: FormState = { ...initial, avatarFile: null };
        writeSettingsSnapshot({
            form: snapshotForm,
            initial: snapshotInitial,
            realm,
            searchQuery,
            scrollY: window.scrollY,
            savedAt: Date.now(),
        });
    }, [form, initial, realm, searchQuery]);

    useEffect(() => {
        const restored = restoredSettingsRef.current;
        if (!restored?.scrollY) return;
        const frame = window.requestAnimationFrame(() => {
            window.scrollTo({ top: restored.scrollY, behavior: "auto" });
        });
        return () => window.cancelAnimationFrame(frame);
    }, []);

    useEffect(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) {
            setFilteredSections([]);
            return;
        }

        const sectionIndex: Record<string, string[]> = {
            profile: ["account", "profile", "avatar", "username", "display name", "bio", form.username, form.displayName, form.bio],
            privacy: [
                "privacy",
                "incognito",
                "online",
                "last seen",
                "read receipts",
                "messages",
                "invites",
                "search visibility",
                "activity visibility",
                "presence visibility",
                form.accountVisibility,
                form.whoCanMessage,
                form.whoCanInviteGroups,
                form.searchVisibility,
                form.activityVisibility,
                form.presenceVisibility,
            ],
            security: ["security", "password", "two step", "2fa", "recovery", "phrase"],
            notifications: [
                "notifications",
                "push",
                "email",
                "sound",
                "messages",
                "spaces",
                "vault",
                "mentions",
                "reactions",
                "security alerts",
                String(form.pushNotifications),
                String(form.emailNotifications),
                String(form.soundEffects),
            ],
            appearance: [
                "appearance",
                "theme",
                "font",
                "language",
                "glow",
                "motion",
                "contrast",
                form.theme,
                form.fontSize,
                form.language,
            ],
            danger: ["danger", "delete", "account deletion", "reset session", "irreversible", "logged in ips", "sessions", "logout all"],
            sentinel: ["sentinel", "oversight", "reports", "blackbox", "realm"],
        };

        setFilteredSections(
            Object.entries(sectionIndex)
                .filter(([, terms]) => terms.some((term) => String(term || "").toLowerCase().includes(query)))
                .map(([section]) => section)
        );
    }, [searchQuery, form]);

    useEffect(() => {
        if (loading || !["settings-privacy", "settings-notifications", "settings-appearance-themes", "settings-appearance-languages"].includes(settingsTarget)) return;
        setSearchQuery("");
        setSettingsSearchOpen(false);
        if (settingsTarget === "settings-appearance-themes") setAppearanceModal("themes");
        if (settingsTarget === "settings-appearance-languages") setAppearanceModal("languages");
    }, [loading, location.key, settingsTarget]);

    useEffect(() => {
        if (loading || searchQuery.trim() || filteredSections.length) return;
        if (!["settings-privacy", "settings-notifications", "settings-appearance"].includes(targetSectionId)) return;
        const frame = window.requestAnimationFrame(() => {
            document.getElementById(targetSectionId)?.scrollIntoView({ block: "start", behavior: "auto" });
        });
        return () => window.cancelAnimationFrame(frame);
    }, [loading, location.key, targetSectionId, searchQuery, filteredSections]);

    // Dirty check vs baseline
    const isDirty = useMemo(
        () => JSON.stringify(canonicalFormState(form)) !== JSON.stringify(canonicalFormState(initial)),
        [form, initial]
    );

    // Unsaved changes guard
    useEffect(() => {
        const handler = (e: BeforeUnloadEvent) => {
            if (logoutInProgressRef.current) return;
            if (!isDirty) return;
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [isDirty]);

    // Username availability check
    useEffect(() => {
        const valid = validateUsername(debouncedUsername).valid;
        const normalized = String(debouncedUsername || "").trim().toLowerCase();
        const changed = normalized && normalized !== String(initial.username || "").trim().toLowerCase();

        if (!valid || !changed) {
            setUsernameAvailable(null);
            return;
        }

        let alive = true;
        (async () => {
            setCheckingUser(true);
            try {
                const ok = await checkUsernameAvailable(normalized);
                if (alive) setUsernameAvailable(ok);
            } catch {
                if (alive) setUsernameAvailable(false);
            } finally {
                if (alive) setCheckingUser(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [debouncedUsername, initial.username]);

    // Avatar constraints
    const [avatarError, setAvatarError] = useState<string | null>(null);
    const maxAvatarMB = 5;
    const allowedAvatarTypes = ["image/jpeg", "image/png", "image/webp"];

    function handleAvatarPick(file?: File) {
        setAvatarError(null);
        if (!file) return;
        if (!allowedAvatarTypes.includes(file.type)) {
            setAvatarError("Allowed types: JPG, PNG, WebP");
            return;
        }
        if (file.size > maxAvatarMB * 1024 * 1024) {
            setAvatarError(`Max size ${maxAvatarMB} MB`);
            return;
        }

        // Simulate upload progress
        setUploadProgress(0);
        const interval = setInterval(() => {
            setUploadProgress((prev) => {
                if (prev >= 100) {
                    clearInterval(interval);
                    const localUrl = URL.createObjectURL(file);
                    setForm((f) => ({ ...f, avatarFile: file, avatarUrl: localUrl }));
                    return 100;
                }
                return prev + 10;
            });
        }, 100);
    }

    function validateUsername(u: string): { valid: boolean; error?: string } {
        if (!u) return { valid: false, error: "Username is required" };
        if (u.length < 3) return { valid: false, error: "At least 3 characters" };
        if (u.length > USERNAME_MAX_LENGTH) return { valid: false, error: `Max ${USERNAME_MAX_LENGTH} characters` };
        if (!/^[a-z0-9_]+$/i.test(u))
            return { valid: false, error: "Only letters, numbers and _" };
        return { valid: true };
    }
    const usernameValidation = validateUsername(form.username);

    // Load real profile on mount (maps server user -> form)
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {

                const res = await getProfile();
                if (!mounted) return;
                if (!res || !res.success) {
                    setSaveMsg({
                        kind: "err",
                        text: res?.message ?? "Failed to load profile",
                    });
                    return;
                }
                setRealm((res as any).realm || null);
                const token = localStorage.getItem("token");
                if (token) {
                    fetch(`${API_BASE}/api/sentinel/me`, {
                        headers: { Authorization: `Bearer ${token}` },
                    })
                        .then((response) => response.ok ? response.json() : null)
                        .then((data) => {
                            if (mounted && data?.realm) setRealm(data.realm);
                        })
                        .catch(() => {});
                }
                const srv: any = res.user ?? {};
                setRecoveryPhraseStatus(
                    srv.recoveryPhraseStatus === "active" || srv.recoveryPhraseStatus === "used"
                        ? srv.recoveryPhraseStatus
                        : srv.recoveryPhraseConfigured
                            ? "active"
                            : "missing"
                );

                const bool = (v: any, d = false) =>
                    typeof v === "boolean" ? v : Boolean(v ?? d);
                const str = (v: any, d = "") =>
                    typeof v === "string" ? v : v == null ? d : String(v);
                const pickSetting = (k: string, def: any) =>
                    srv.settings && srv.settings[k] !== undefined
                        ? srv.settings[k]
                        : def;

                // prefer values from settings for profile fields, fallback to top level or default
                const displayName = srv.displayName ?? "";
                const bio = srv.bio ?? "";
                const avatarUrlFromSettings = srv.avatarUrl ?? initialForm.avatarUrl;

                const mapped: Partial<FormState> = {
                    username: str(srv.username, ""),
                    displayName: str(displayName, ""),
                    bio: str(bio, ""),
                    avatarUrl: str(avatarUrlFromSettings, initialForm.avatarUrl),
                    email: str(srv.email, ""),
                    avatarFile: null as File | null,

                    // privacy
                    accountVisibility:
                        (pickSetting(
                            "accountVisibility",
                            initialForm.accountVisibility
                        ) as FormState["accountVisibility"]) ?? "public",
                    incognitoMode: bool(pickSetting("incognitoMode", pickSetting("killSwitch", false)), false),
                    showOnline: bool(pickSetting("showOnline", true), true),
                    lastSeen:
                        (pickSetting("lastSeen", "Everyone") as LastSeenVisibility) ??
                        "Everyone",
                    readReceipts: bool(pickSetting("readReceipts", true), true),
                    whoCanMessage: normalizeMessageReach(pickSetting("whoCanMessage", "Anyone")),
                    whoCanInviteGroups: normalizeMessageReach(pickSetting("whoCanInviteGroups", "Anyone")),
                    searchVisibility: normalizeAudience(pickSetting("searchVisibility", "Everyone")),
                    activityVisibility: normalizeAudience(pickSetting("activityVisibility", "Friends"), "Friends"),
                    presenceVisibility: normalizeAudience(pickSetting("presenceVisibility", "Friends"), "Friends"),

                    // security
                    twoFactorAuth: bool(pickSetting("twoFactorAuth", false), false),
                    loginAlerts: bool(pickSetting("loginAlerts", true), true),

                    // notifications
                    pushNotifications: bool(
                        pickSetting("pushNotifications", true),
                        true
                    ),
                    emailNotifications: bool(
                        pickSetting("emailNotifications", false),
                        false
                    ),
                    soundEffects: bool(pickSetting("soundEffects", true), true),
                    notifyMessages: bool(pickSetting("notifyMessages", true), true),
                    notifySpaces: bool(pickSetting("notifySpaces", true), true),
                    notifyVault: bool(pickSetting("notifyVault", true), true),
                    notifyMentions: bool(pickSetting("notifyMentions", true), true),
                    notifyReactions: bool(pickSetting("notifyReactions", true), true),
                    notifySecurity: bool(pickSetting("notifySecurity", true), true),

                    // appearance
                    theme: normalizeThemeName(String(pickSetting("theme", initialForm.theme) || "Auto")),
                    fontSize:
                        (pickSetting(
                            "fontSize",
                            initialForm.fontSize
                        ) as FormState["fontSize"]) ?? "Medium",
                    glow: bool(pickSetting("glow", initialForm.glow), initialForm.glow),
                    language: normalizeLanguageCode(pickSetting("language", initialForm.language)),
                    visibleThemes: normalizeThemeList(
                        pickSetting("visibleThemes", initialForm.visibleThemes),
                        DEFAULT_VISIBLE_THEMES
                    ),
                    visibleLanguages: normalizeLanguageList(
                        pickSetting("visibleLanguages", initialForm.visibleLanguages),
                        DEFAULT_VISIBLE_LANGUAGES
                    ),
                    reduceMotion: bool(pickSetting("reduceMotion", false), false),
                    highContrast: bool(pickSetting("highContrast", false), false),

                    // vault
                    vaultDefaultView: normalizeVaultDefaultView(pickSetting("vaultDefaultView", "both")),
                    vaultAutosave: bool(pickSetting("vaultAutosave", true), true),
                    vaultDefaultSharePermission: normalizeVaultSharePermission(pickSetting("vaultDefaultSharePermission", "view")),
                    vaultRecycleRetentionDays: Number(pickSetting("vaultRecycleRetentionDays", 30)) || 30,
                    vaultExportFormat: normalizeVaultExportFormat(pickSetting("vaultExportFormat", "json")),

                    // spaces
                    spacesDefaultVisibility: normalizeSpacesDefaultVisibility(pickSetting("spacesDefaultVisibility", "public")),
                    spacesEventReminders: bool(pickSetting("spacesEventReminders", true), true),
                    spacesJoinRequestAlerts: bool(pickSetting("spacesJoinRequestAlerts", true), true),
                    spacesArchiveAlerts: bool(pickSetting("spacesArchiveAlerts", true), true),
                    spacesPresenceVisibility: normalizeSpacesPresenceVisibility(pickSetting("spacesPresenceVisibility", "members")),

                    // experimental
                    aiSuggestions: bool(pickSetting("aiSuggestions", false), false),
                    betaFeatures: bool(pickSetting("betaFeatures", false), false),
                };

                // Override theme with localStorage (source of truth for instant apply)
                const localTheme = localStorage.getItem("theme");
                if (localTheme) {
                    (mapped as any).theme = normalizeThemeName(localTheme);
                }
                mapped.visibleThemes = normalizeThemeList(mapped.visibleThemes, DEFAULT_VISIBLE_THEMES);
                mapped.visibleLanguages = normalizeLanguageList(mapped.visibleLanguages, DEFAULT_VISIBLE_LANGUAGES);

                writeAppearanceStorage(buildAppearancePayload(mapped as FormState));

                setForm(mapped as FormState);
                setInitial(mapped as FormState);
            } catch (err: any) {
                console.error("Load profile error", err);
                if (err && (err.status === 401 || err.status === 403)) {
                    clearLocalSession(false);
                    navigate("/");
                    return;
                }
                setSaveMsg({ kind: "err", text: err?.message ?? "Failed to load profile" });
            }
            finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        })();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },
        [navigate]);

    async function onSave(allowUsernameChange = false) {
        setSaving(true);
        setSaveMsg(null);
        try {
            const normalizedUsername = String(form.username || "").trim().toLowerCase();
            const initialUsername = String(initial.username || "").trim().toLowerCase();
            const usernameChanged = !isPresence && normalizedUsername !== initialUsername;
            if (usernameChanged && (checkingUser || usernameAvailable !== true)) {
                throw new Error(checkingUser ? "Wait for username availability to finish." : "Choose an available username before saving.");
            }
            if (form.avatarFile) {
                throw new Error("Avatar preview is not saved yet. Reset it or wire production media storage first.");
            }

            if (usernameChanged && !allowUsernameChange) {
                setSaving(false);
                openConfirm({
                    title: "Change username?",
                    message: "Old mentions and profile links may stop pointing cleanly to you after this change.",
                    confirmLabel: "Change username",
                    onConfirm: () => onSave(true),
                });
                return;
            }

            let avatarUrl = form.avatarUrl;

            // Build payload: this matches routes/profile.js expectations.
            // This account type does not expose public visibility or account controls here.
            const payload: any = {
                security: {
                    twoFactorAuth: Boolean(form.twoFactorAuth ?? false),
                    loginAlerts: Boolean(form.loginAlerts ?? true),
                },
                notifications: {
                    pushNotifications: Boolean(form.pushNotifications ?? true),
                    emailNotifications: Boolean(form.emailNotifications ?? false),
                    soundEffects: Boolean(form.soundEffects ?? true),
                    notifyMessages: Boolean(form.notifyMessages ?? true),
                    notifySpaces: Boolean(form.notifySpaces ?? true),
                    notifyVault: Boolean(form.notifyVault ?? true),
                    notifyMentions: Boolean(form.notifyMentions ?? true),
                    notifyReactions: Boolean(form.notifyReactions ?? true),
                    notifySecurity: Boolean(form.notifySecurity ?? true),
                },
                appearance: buildAppearancePayload(form),
                vault: {
                    vaultDefaultView: form.vaultDefaultView,
                    vaultAutosave: Boolean(form.vaultAutosave ?? true),
                    vaultDefaultSharePermission: form.vaultDefaultSharePermission,
                    vaultRecycleRetentionDays: Number(form.vaultRecycleRetentionDays || 30),
                    vaultExportFormat: form.vaultExportFormat,
                },
                spaces: {
                    spacesDefaultVisibility: form.spacesDefaultVisibility,
                    spacesEventReminders: Boolean(form.spacesEventReminders ?? true),
                    spacesJoinRequestAlerts: Boolean(form.spacesJoinRequestAlerts ?? true),
                    spacesArchiveAlerts: Boolean(form.spacesArchiveAlerts ?? true),
                    spacesPresenceVisibility: form.spacesPresenceVisibility,
                },
                experimental: {
                    aiSuggestions: Boolean(form.aiSuggestions ?? false),
                    betaFeatures: Boolean(form.betaFeatures ?? false),
                },
            };

            if (!isPresence) {
                payload.username = normalizedUsername;
                payload.displayName = form.displayName ? String(form.displayName).trim() : undefined;
                payload.bio = form.bio ? String(form.bio).trim() : undefined;
                payload.avatarUrl = avatarUrl ? String(avatarUrl) : undefined;
                payload.email = form.email ? String(form.email).trim() : "";
                payload.cropRadius = AVATAR_SOFT_RADIUS;
                payload.privacy = {
                    accountVisibility: (form.accountVisibility as FormState["accountVisibility"]) ?? "public",
                    incognitoMode: Boolean(form.incognitoMode ?? false),
                    showOnline: Boolean(form.showOnline ?? true),
                    lastSeen: (form.lastSeen as LastSeenVisibility) ?? "Everyone",
                    readReceipts: Boolean(form.readReceipts ?? true),
                    whoCanMessage: normalizeMessageReach(form.whoCanMessage),
                    whoCanInviteGroups: normalizeMessageReach(form.whoCanInviteGroups),
                    searchVisibility: form.searchVisibility,
                    activityVisibility: form.activityVisibility,
                    presenceVisibility: form.presenceVisibility,
                };
            } else {
                payload.displayName = form.displayName ? String(form.displayName).trim() : "";
                payload.bio = form.bio ? String(form.bio).trim() : "";
            }

            // remove undefined top level keys
            Object.keys(payload).forEach(
                (k) => payload[k] === undefined && delete payload[k]
            );

            const res = await saveSettings(payload);
            if (!res || !(res as any).success) {
                throw new Error((res as any)?.message || "Failed to save");
            }

            localStorage.setItem("blackspace.settings.privacy", JSON.stringify(payload.privacy || {}));
            localStorage.setItem("blackspace.settings.notifications", JSON.stringify(payload.notifications || {}));
            localStorage.setItem("blackspace.settings.vault", JSON.stringify(payload.vault || {}));
            localStorage.setItem("blackspace.settings.spaces", JSON.stringify(payload.spaces || {}));
            writeAppearanceStorage(payload.appearance);

            // If server sent back updated user, remap from that to be safe
            const serverUser: any = (res as any).user;
            if (serverUser) {
                const srv: any = serverUser;
                const bool = (v: any, d = false) =>
                    typeof v === "boolean" ? v : Boolean(v ?? d);
                const str = (v: any, d = "") =>
                    typeof v === "string" ? v : v == null ? d : String(v);
                const pickSetting = (k: string, def: any) =>
                    srv.settings && srv.settings[k] !== undefined
                        ? srv.settings[k]
                        : def;

                const displayName = srv.displayName ?? "";
                const bio = srv.bio ?? "";
                const avatarUrlFromSettings = srv.avatarUrl ?? initialForm.avatarUrl;
                const mapped: Partial<FormState> = {
                    username: str(srv.username, ""),
                    displayName: str(displayName, ""),
                    bio: str(bio, ""),
                    avatarUrl: str(avatarUrlFromSettings, initialForm.avatarUrl),
                    email: str(srv.email, ""),
                    avatarFile: null as File | null,

                    accountVisibility:
                        (pickSetting(
                            "accountVisibility",
                            payload.privacy?.accountVisibility || initialForm.accountVisibility
                        ) as FormState["accountVisibility"]) ?? "public",
                    incognitoMode: bool(pickSetting("incognitoMode", pickSetting("killSwitch", false)), false),
                    showOnline: bool(pickSetting("showOnline", true), true),
                    lastSeen:
                        (pickSetting("lastSeen", "Everyone") as LastSeenVisibility) ??
                        "Everyone",
                    readReceipts: bool(pickSetting("readReceipts", true), true),
                    whoCanMessage: normalizeMessageReach(pickSetting("whoCanMessage", "Anyone")),
                    whoCanInviteGroups: normalizeMessageReach(pickSetting("whoCanInviteGroups", "Anyone")),
                    searchVisibility: normalizeAudience(pickSetting("searchVisibility", "Everyone")),
                    activityVisibility: normalizeAudience(pickSetting("activityVisibility", "Friends"), "Friends"),
                    presenceVisibility: normalizeAudience(pickSetting("presenceVisibility", "Friends"), "Friends"),

                    twoFactorAuth: bool(pickSetting("twoFactorAuth", false), false),
                    loginAlerts: bool(pickSetting("loginAlerts", true), true),

                    pushNotifications: bool(
                        pickSetting("pushNotifications", true),
                        true
                    ),
                    emailNotifications: bool(
                        pickSetting("emailNotifications", false),
                        false
                    ),
                    soundEffects: bool(pickSetting("soundEffects", true), true),
                    notifyMessages: bool(pickSetting("notifyMessages", true), true),
                    notifySpaces: bool(pickSetting("notifySpaces", true), true),
                    notifyVault: bool(pickSetting("notifyVault", true), true),
                    notifyMentions: bool(pickSetting("notifyMentions", true), true),
                    notifyReactions: bool(pickSetting("notifyReactions", true), true),
                    notifySecurity: bool(pickSetting("notifySecurity", true), true),

                    theme: normalizeThemeName(String(pickSetting("theme", initialForm.theme) || "Auto")),
                    fontSize:
                        (pickSetting(
                            "fontSize",
                            initialForm.fontSize
                        ) as FormState["fontSize"]) ?? "Medium",
                    glow: bool(pickSetting("glow", initialForm.glow), initialForm.glow),
                    language: normalizeLanguageCode(pickSetting("language", payload.appearance.language || initialForm.language)),
                    visibleThemes: normalizeThemeList(
                        pickSetting("visibleThemes", payload.appearance.visibleThemes || initialForm.visibleThemes),
                        DEFAULT_VISIBLE_THEMES
                    ),
                    visibleLanguages: normalizeLanguageList(
                        pickSetting("visibleLanguages", payload.appearance.visibleLanguages || initialForm.visibleLanguages),
                        DEFAULT_VISIBLE_LANGUAGES
                    ),
                    reduceMotion: bool(pickSetting("reduceMotion", false), false),
                    highContrast: bool(pickSetting("highContrast", false), false),

                    vaultDefaultView: normalizeVaultDefaultView(pickSetting("vaultDefaultView", "both")),
                    vaultAutosave: bool(pickSetting("vaultAutosave", true), true),
                    vaultDefaultSharePermission: normalizeVaultSharePermission(pickSetting("vaultDefaultSharePermission", "view")),
                    vaultRecycleRetentionDays: Number(pickSetting("vaultRecycleRetentionDays", 30)) || 30,
                    vaultExportFormat: normalizeVaultExportFormat(pickSetting("vaultExportFormat", "json")),

                    spacesDefaultVisibility: normalizeSpacesDefaultVisibility(pickSetting("spacesDefaultVisibility", "public")),
                    spacesEventReminders: bool(pickSetting("spacesEventReminders", true), true),
                    spacesJoinRequestAlerts: bool(pickSetting("spacesJoinRequestAlerts", true), true),
                    spacesArchiveAlerts: bool(pickSetting("spacesArchiveAlerts", true), true),
                    spacesPresenceVisibility: normalizeSpacesPresenceVisibility(pickSetting("spacesPresenceVisibility", "members")),

                    aiSuggestions: bool(pickSetting("aiSuggestions", false), false),
                    betaFeatures: bool(pickSetting("betaFeatures", false), false),
                };

                setForm(mapped as FormState);
                setInitial(mapped as FormState);
                writeAppearanceStorage(buildAppearancePayload(mapped as FormState));
            } else {
                // fallback: commit local baseline
                setForm((f) => ({ ...f, avatarUrl }));
                setInitial({ ...form, avatarUrl });
            }

            showNotice("ok", "Settings saved.");
        } catch (e: any) {
            console.error("Save error:", e);
            showNotice("err", e?.message || "Could not save changes");
        } finally {
            setSaving(false);
        }
    }

    function onReset() {
        openConfirm({
            title: "Discard changes?",
            message: "Your unsaved Settings edits will be restored to the last saved version.",
            confirmLabel: "Discard changes",
            onConfirm: () => {
                setForm({ ...initial });
                setAvatarError(null);
                setUsernameAvailable(null);
                setUploadProgress(0);
                showNotice("ok", "Unsaved changes discarded.");
            },
        });
    }

    const handleLogout = async (allDevices: boolean) => {
        try {
            logoutInProgressRef.current = true;
            setInitial({ ...form });
            if (allDevices) {
                await logoutAllDevices();
            }
            clearLocalSession(allDevices);
            setShowLogoutModal(false);
            window.location.replace("/login");
        } catch (error) {
            console.error("Logout error:", error);
            logoutInProgressRef.current = false;
            setShowLogoutModal(false);
            showNotice("err", error instanceof Error ? error.message : "Could not logout from all devices.");
        } finally {
        }
    };

    const confirmLogoutAllSessions = () => {
        openConfirm({
            title: "Log out all sessions?",
            message: "Every logged in device will be signed out, including this one.",
            confirmLabel: "Log out all",
            tone: "danger",
            onConfirm: () => handleLogout(true),
        });
    };

    const confirmLogoutSession = (session: AccountSession) => {
        const label = session.current ? "this device" : session.deviceLabel || "this session";
        openConfirm({
            title: `Log out ${label}?`,
            message: session.revocable === false
                ? "This older session can only be ended from this browser."
                : `${session.ip || "Unknown IP"} will be signed out immediately.`,
            confirmLabel: session.current ? "Log out this device" : "Log out session",
            tone: "danger",
            onConfirm: async () => {
                if (session.revocable === false) {
                    clearLocalSession(false);
                    window.location.replace("/login");
                    return;
                }

                setRevokingSessionId(session.id);
                try {
                    const res = await revokeAccountSession(session.id);
                    if (session.current || res.current) {
                        clearLocalSession(false);
                        window.location.replace("/login");
                        return;
                    }
                    showNotice("ok", res.message || "Session logged out.");
                    await loadAccountSessions(true);
                } finally {
                    setRevokingSessionId(null);
                }
            },
        });
    };

    const resetLocalSession = () => {
        openConfirm({
            title: "Reset session here?",
            message: "This signs you out everywhere and clears this browser session.",
            confirmLabel: "Reset and sign out",
            tone: "danger",
            onConfirm: () => handleLogout(true),
        });
    };

    const deleteCurrentAccount = async () => {
        if (isPresence) return;
        openConfirm({
            title: "Delete account forever?",
            message: "This permanently deletes your account data and signs you out. This cannot be undone.",
            confirmLabel: "Delete account",
            tone: "danger",
            requireText: "DELETE MY ACCOUNT",
            requirePassword: true,
            onConfirm: async () => {
                try {
                    logoutInProgressRef.current = true;
                    await deleteAccount({ currentPassword: confirmPasswordRef.current });
                    clearLocalSession(true);
                    try {
                        sessionStorage.setItem(
                            PUBLIC_AUTH_NOTICE_KEY,
                            JSON.stringify({
                                kind: "ok",
                                title: "Account deleted",
                                message: "Your Blackspace account was deleted successfully.",
                            })
                        );
                    } catch {
                        // URL notice below is the cross-browser fallback.
                    }
                    window.location.replace("/login?notice=account-deleted");
                } catch (e: any) {
                    logoutInProgressRef.current = false;
                    throw new Error(e?.message || "Account deletion failed.");
                }
            },
        });
    };

    const closePasswordDialog = () => {
        if (passwordSaving) return;
        setPasswordDialogOpen(false);
        setPasswordError("");
        setPasswordForm({ currentPassword: "", recoveryPhrase: "", newPassword: "", confirmPassword: "" });
    };

    const submitPasswordChange = async () => {
        const currentPassword = passwordForm.currentPassword;
        const recoveryPhrase = passwordForm.recoveryPhrase;
        const newPassword = passwordForm.newPassword;
        const confirmPassword = passwordForm.confirmPassword;

        if (!currentPassword || !recoveryPhrase || !newPassword || !confirmPassword) {
            setPasswordError("Current password, recovery phrase, and both new password fields are required.");
            return;
        }
        if (recoveryPhrase.trim().split(/\s+/).filter(Boolean).length !== 12) {
            setPasswordError("Enter the complete 12-word recovery phrase.");
            return;
        }
        if (newPassword.length < 8 || newPassword.length > 100) {
            setPasswordError("New password must be 8-100 characters.");
            return;
        }
        if (!/[A-Za-z]/.test(newPassword) || !/\d/.test(newPassword)) {
            setPasswordError("New password needs at least one letter and one number.");
            return;
        }
        if (newPassword !== confirmPassword) {
            setPasswordError("New passwords do not match.");
            return;
        }
        if (newPassword === currentPassword) {
            setPasswordError("New password must be different from the current password.");
            return;
        }

        setPasswordSaving(true);
        setPasswordError("");
        try {
            const res = await changePassword({ currentPassword, recoveryPhrase, newPassword });
            if (!res.success) throw new Error(res.message || "Could not change password.");
            if (res.token) localStorage.setItem("token", res.token);
            setPasswordDialogOpen(false);
            setPasswordForm({ currentPassword: "", recoveryPhrase: "", newPassword: "", confirmPassword: "" });
            showNotice("ok", res.message || "Password changed.");
        } catch (error: any) {
            setPasswordError(error?.message || "Could not change password.");
        } finally {
            setPasswordSaving(false);
        }
    };

    const loadNewRecoveryPhrase = async () => {
        const generationId = ++recoveryGenerationRef.current;
        setRecoveryGenerating(true);
        setRecoveryError("");
        setRecoveryPhraseConfirmed(false);
        try {
            const phrase = await generateRecoveryPhrase();
            if (generationId !== recoveryGenerationRef.current) return;
            setRecoveryGeneratedPhrase(phrase);
        } catch (error: any) {
            if (generationId !== recoveryGenerationRef.current) return;
            setRecoveryError(error?.message || "Could not generate a recovery phrase.");
        } finally {
            if (generationId === recoveryGenerationRef.current) setRecoveryGenerating(false);
        }
    };

    const openRecoveryModal = () => {
        setRecoveryCurrentPassword("");
        setRecoveryPreviousPhrase("");
        setRecoveryGeneratedPhrase("");
        setRecoveryPhraseConfirmed(false);
        setRecoveryError("");
        setRecoveryModalOpen(true);
        void loadNewRecoveryPhrase();
    };

    const closeRecoveryModal = () => {
        if (recoverySaving) return;
        recoveryGenerationRef.current += 1;
        setRecoveryModalOpen(false);
        setRecoveryCurrentPassword("");
        setRecoveryPreviousPhrase("");
        setRecoveryGeneratedPhrase("");
        setRecoveryPhraseConfirmed(false);
        setRecoveryGenerating(false);
        setRecoveryError("");
    };

    const saveRecoveryPhrase = async () => {
        if (!recoveryCurrentPassword) {
            setRecoveryError("Enter your current password.");
            return;
        }
        if (recoveryConfigured && recoveryPreviousPhrase.trim().split(/\s+/).filter(Boolean).length !== 12) {
            setRecoveryError("Enter the complete current recovery phrase before rotating it.");
            return;
        }
        if (!recoveryGeneratedPhrase || !recoveryPhraseConfirmed) {
            setRecoveryError("Confirm the requested recovery words before saving.");
            return;
        }

        setRecoverySaving(true);
        setRecoveryError("");
        try {
            const res = await setRecoveryPhrase({
                currentPassword: recoveryCurrentPassword,
                currentRecoveryPhrase: recoveryConfigured ? recoveryPreviousPhrase : undefined,
                recoveryPhrase: recoveryGeneratedPhrase,
            });
            if (!res?.success) throw new Error(res?.message || "Recovery phrase save failed.");
            setRecoveryPhraseStatus("active");
            setRecoveryCurrentPassword("");
            setRecoveryPreviousPhrase("");
            setRecoveryGeneratedPhrase("");
            setRecoveryPhraseConfirmed(false);
            setRecoveryModalOpen(false);
            showNotice("ok", res.message || "Recovery phrase secured.");
        } catch (error: any) {
            setRecoveryError(error?.message || "Could not save recovery phrase.");
        } finally {
            setRecoverySaving(false);
        }
    };

    const openTwoFactorModal = () => {
        setTwoFactorError("");
        setTwoFactorModalOpen(true);
    };

    const closeTwoFactorModal = () => {
        if (twoFactorSaving) return;
        setTwoFactorModalOpen(false);
        setTwoFactorError("");
    };

    const saveTwoFactorState = async (enabled: boolean) => {
        setTwoFactorSaving(true);
        setTwoFactorError("");
        try {
            const res = await saveSettings({ security: { twoFactorAuth: enabled } });
            if (!res || !(res as any).success) throw new Error((res as any)?.message || "2FA save failed.");
            const serverEnabled = Boolean(((res as any).user?.settings?.twoFactorAuth) ?? enabled);
            const next = hydrateFormState({ ...formRef.current, twoFactorAuth: serverEnabled });
            const nextInitial = hydrateFormState({ ...initialRef.current, twoFactorAuth: serverEnabled });
            setForm(next);
            setInitial(nextInitial);
            setTwoFactorModalOpen(false);
            showNotice("ok", serverEnabled ? "2FA setup saved." : "2FA setup removed.");
        } catch (error: any) {
            setTwoFactorError(error?.message || "Could not save 2FA.");
        } finally {
            setTwoFactorSaving(false);
        }
    };

    const searchActive = searchQuery.trim().length > 0;
    const hasSearchResults = !searchActive || filteredSections.length > 0;
    const usernameChanged = !isPresence && String(form.username || "").trim().toLowerCase() !== String(initial.username || "").trim().toLowerCase();
    const usernameAvailabilityBlocked = usernameChanged && (checkingUser || usernameAvailable !== true);
    const saveDisabled = !isDirty || saving || (usernameTouched && !usernameValidation.valid) || usernameAvailabilityBlocked || Boolean(avatarError) || Boolean(form.avatarFile);
    const canShowSection = (section: string) => filteredSections.length === 0 || filteredSections.includes(section);
    const tinyButtonClass = "rounded-xl border border-cyan-400/20 bg-cyan-950/20 px-3 py-2 text-xs font-black text-cyan-100 transition hover:bg-cyan-950/35";
    const dangerButtonClass = "rounded-xl border border-rose-400/20 bg-rose-950/25 px-3 py-2 text-xs font-black text-rose-100 transition hover:bg-rose-950/40";
    const accountInputClass = "w-full rounded-[0.95rem] border-0 border-b border-white/[0.055] bg-white/[0.025] px-3 py-2.5 text-sm font-semibold text-white outline-none transition placeholder:text-neutral-600 focus:border-cyan-200/55 focus:bg-cyan-300/[0.035] focus:shadow-[inset_0_-1px_0_rgba(103,232,249,0.36),0_0_20px_rgba(34,211,238,0.08)]";
    const accountBioInputClass = "w-full rounded-[1.05rem] border-0 border-b border-white/[0.055] bg-[linear-gradient(135deg,rgba(255,255,255,0.035),rgba(34,211,238,0.018),rgba(0,0,0,0.12))] px-3 py-3 text-sm font-semibold leading-6 text-white outline-none transition placeholder:text-neutral-600 focus:border-cyan-200/55 focus:bg-cyan-300/[0.035] focus:shadow-[inset_0_-1px_0_rgba(103,232,249,0.36),0_0_24px_rgba(34,211,238,0.09)]";
    const recoveryConfigured = recoveryPhraseStatus === "active";
    const twoFactorConfigured = Boolean(form.twoFactorAuth);
    const applyAppearanceDraft = (next: Partial<Pick<FormState, "theme" | "fontSize" | "glow" | "reduceMotion" | "highContrast" | "language" | "visibleThemes" | "visibleLanguages">>) => {
        const updated = hydrateFormState({ ...formRef.current, ...next });
        if (JSON.stringify(canonicalFormState(updated)) === JSON.stringify(canonicalFormState(formRef.current))) return;
        formRef.current = updated;
        setForm(updated);
        writeAppearanceStorage(buildAppearancePayload(updated));
    };

    const visibleThemeNames = useMemo(
        () => normalizeThemeList(form.visibleThemes, DEFAULT_VISIBLE_THEMES),
        [form.visibleThemes]
    );
    const visibleThemeGroups = useMemo(
        () => THEME_GROUPS
            .map((group) => ({
                ...group,
                themes: group.themes.filter((themeName) => visibleThemeNames.includes(themeName)),
            }))
            .filter((group) => group.themes.length > 0),
        [visibleThemeNames]
    );
    const visibleLanguageCodes = useMemo(
        () => normalizeLanguageList(form.visibleLanguages, DEFAULT_VISIBLE_LANGUAGES),
        [form.visibleLanguages]
    );
    const appearanceDirty = useMemo(
        () => appearanceSignature(form) !== appearanceSignature(initial),
        [form, initial]
    );

    const toggleVisibleTheme = (themeName: AppearanceTheme) => {
        const activeForm = formRef.current;
        const current = normalizeThemeList(activeForm.visibleThemes, DEFAULT_VISIBLE_THEMES);
        if (themeName === activeForm.theme && current.includes(themeName)) {
            showNotice("ok", "Active theme stays visible.");
            return;
        }
        if (current.includes(themeName) && current.length <= 1) {
            showNotice("ok", "Keep at least one theme visible.");
            return;
        }
        const next = current.includes(themeName)
            ? current.filter((theme) => theme !== themeName)
            : [...current, themeName];
        if (JSON.stringify(next) === JSON.stringify(current)) return;
        applyAppearanceDraft({ visibleThemes: next });
    };

    const selectTheme = (themeName: AppearanceTheme) => {
        if (themeName === formRef.current.theme) return;
        applyAppearanceDraft({ theme: themeName });
    };

    const toggleVisibleLanguage = (code: LanguageCode) => {
        const activeForm = formRef.current;
        const current = normalizeLanguageList(activeForm.visibleLanguages, DEFAULT_VISIBLE_LANGUAGES);
        if (code === activeForm.language && current.includes(code)) {
            showNotice("ok", "Active language stays visible.");
            return;
        }
        if (current.includes(code) && current.length <= 1) {
            showNotice("ok", "Keep at least one language visible.");
            return;
        }
        const next = current.includes(code)
            ? current.filter((language) => language !== code)
            : [...current, code];
        if (JSON.stringify(next) === JSON.stringify(current)) return;
        applyAppearanceDraft({ visibleLanguages: next });
    };

    const selectLanguage = (code: LanguageCode) => {
        if (code === formRef.current.language) return;
        applyAppearanceDraft({ language: code });
    };

    const currentAppearanceDirty = () =>
        appearanceSignature(formRef.current) !== appearanceSignature(initialRef.current);

    const closeAppearanceModal = () => {
        if (currentAppearanceDirty()) {
            showNotice("err", "Save appearance before closing.");
            return;
        }
        setAppearanceModal(null);
    };

    const saveAppearanceOnly = async () => {
        if (!currentAppearanceDirty()) {
            setAppearanceModal(null);
            return;
        }

        const current = formRef.current;
        const appearance = buildAppearancePayload(current);
        setSaving(true);
        setSaveMsg(null);
        try {
            const res = await saveSettings({ appearance });
            if (!res || !(res as any).success) {
                throw new Error((res as any)?.message || "Appearance save failed.");
            }

            const persisted = normalizePersistedAppearance((res as any).user?.settings, appearance);
            if (!appearanceMatches(persisted, appearance)) {
                throw new Error("Appearance saved, but the server returned different theme visibility. Redeploy the backend and try again.");
            }

            const nextForm = hydrateFormState({ ...formRef.current, ...persisted });
            const nextInitial = hydrateFormState({ ...initialRef.current, ...persisted });
            formRef.current = nextForm;
            initialRef.current = nextInitial;
            setForm(nextForm);
            setInitial(nextInitial);
            writeAppearanceStorage(persisted);
            setAppearanceModal(null);
            showNotice("ok", "Appearance saved.");
        } catch (error: any) {
            showNotice("err", error?.message || "Appearance save failed.");
        } finally {
            setSaving(false);
        }
    };

    const renderThemeSwatches = (theme: (typeof THEME_OPTIONS)[number], className: string) => {
        return (
            <span className={className}>
                {theme.swatches.map((color) => (
                    <span
                        key={color}
                        className="h-2.5 w-2.5 rounded-[0.04rem] border border-black/70"
                        style={{ backgroundColor: color }}
                    />
                ))}
            </span>
        );
    };

    function renderChoice<T extends string>(
        value: T,
        onChange: (value: T) => void,
        options: readonly T[],
        labels: Partial<Record<T, string>> = {},
        disabled = false
    ) {
        return (
            <div className={classNames("flex shrink-0 flex-nowrap justify-end gap-1 rounded-xl bg-black/45 p-1", disabled && "opacity-50")}>
                {options.map((option) => {
                    const selected = value === option;
                    return (
                        <button
                            key={option}
                            type="button"
                            disabled={disabled}
                            onClick={() => onChange(option)}
                            aria-pressed={selected}
                            className={classNames(
                                "rounded-lg px-2.5 py-1.5 text-[11px] font-black transition disabled:cursor-not-allowed",
                                selected
                                    ? "bg-violet-300/18 text-violet-50 shadow-[0_0_18px_rgba(167,139,250,0.18)]"
                                    : "text-neutral-500 hover:bg-white/[0.04] hover:text-neutral-200"
                            )}
                        >
                            {labels[option] || productChoiceLabel(option)}
                        </button>
                    );
                })}
            </div>
        );
    }

    function renderPrivacyControlRow(item: {
        label: string;
        helper: React.ReactNode;
        right: React.ReactNode;
        muted?: boolean;
    }) {
        return (
            <div
                key={item.label}
                className={classNames(
                    "group relative overflow-hidden rounded-[1.15rem] bg-white/[0.012] px-3.5 py-3.5 transition hover:bg-violet-950/[0.035]",
                    item.muted && "opacity-70"
                )}
            >
                <div className="relative grid grid-cols-[minmax(5.75rem,1fr)_auto] items-center gap-x-3 gap-y-0.5">
                    <span className="min-w-0 text-sm font-semibold leading-5 text-neutral-100">
                        {item.label}
                    </span>
                    <div className="min-w-0 justify-self-end">
                        {item.right}
                    </div>
                    <span className="col-span-2 block text-xs font-semibold leading-4 text-neutral-600">
                        {item.helper}
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div className="bs-mobile-settings h-screen flex flex-col bg-[radial-gradient(circle_at_0%_0%,rgba(34,211,238,0.10),transparent_30%),radial-gradient(circle_at_100%_8%,rgba(244,63,94,0.08),transparent_28%),#020303] text-white">
            <SettingsToast message={saveMsg} />
            <main className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
                <div className="mx-auto max-w-2xl space-y-5 pb-24">
                    <header ref={settingsSearchRef} className="relative">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <h1 className="bs-message-page-title">Settings</h1>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => setSettingsSearchOpen((open) => !open)}
                                    className={`bs-home-mobile-command ${settingsSearchOpen || searchQuery ? "is-active" : ""}`}
                                    aria-label="Search settings"
                                    aria-expanded={settingsSearchOpen}
                                >
                                    <Search size={18} />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowLogoutModal(true)}
                                    className="bs-settings-header-logout grid h-11 w-11 place-items-center rounded-2xl border border-white/[0.08] bg-black/60 text-rose-200 transition hover:border-rose-400/30 hover:bg-rose-950/25 md:h-9 md:w-9 md:rounded-none md:border-transparent md:bg-transparent md:hover:border-transparent md:hover:bg-transparent"
                                    aria-label="Sign out"
                                >
                                    <LogOut size={17} />
                                </button>
                            </div>
                        </div>
                        {settingsSearchOpen && (
                            <div className="mt-3 rounded-2xl border border-cyan-400/15 bg-black/95 p-2 shadow-[0_18px_70px_rgba(0,0,0,0.42)] backdrop-blur-xl" role="search" aria-label="Search settings">
                                <form
                                    className="bs-home-mobile-search-form flex items-center gap-2"
                                    onSubmit={(event) => {
                                        event.preventDefault();
                                        settingsSearchInputRef.current?.blur();
                                    }}
                                >
                                    <Search size={17} className="shrink-0 text-cyan-200" />
                                    <input
                                        ref={settingsSearchInputRef}
                                        type="search"
                                        value={searchQuery}
                                        onChange={(event) => setSearchQuery(event.target.value)}
                                        placeholder="Search settings"
                                        className="bs-mobile-search-input min-w-0 flex-1 bg-transparent text-sm font-semibold outline-none"
                                    />
                                    {searchQuery && (
                                        <button
                                            type="button"
                                            onClick={() => setSearchQuery("")}
                                            className="grid h-8 w-8 place-items-center rounded-md border border-neutral-800 bg-black/45 text-neutral-400"
                                            aria-label="Clear search"
                                        >
                                            <X size={15} />
                                        </button>
                                    )}
                                </form>
                            </div>
                        )}
                    </header>

                    {/* ✅ LOADING SPINNER */}
                    {loading && <SettingsSkeleton />}

                    {/* ✅ CONTENT - SHOW ONLY WHEN NOT LOADING */}
                    {!loading && (
                        <>
                            {!hasSearchResults && <EmptySearchState query={searchQuery.trim()} />}

                            {isPresence && (
                                <Section id="settings-presence-profile" title="Self Profile" description="Private identity fields." icon={<UserRound size={18} />} tone="neutral" motion="shimmer" compact>
                                    <SettingRow
                                        label="Username"
                                        right={
                                            <input
                                                value={form.username}
                                                disabled
                                                className="w-full rounded-2xl border border-neutral-800 bg-neutral-950 px-3 py-2 text-sm text-neutral-500 outline-none sm:w-44"
                                            />
                                        }
                                        helper={<p className="text-xs text-neutral-500">Read-only here. Backend identity mapping remains the source of truth.</p>}
                                    />
                                    <SettingRow
                                        label="Display name"
                                        right={
                                            <input
                                                value={form.displayName}
                                                onChange={(event) => setForm((current) => ({ ...current, displayName: event.target.value.slice(0, 50) }))}
                                                maxLength={50}
                                                className="w-full rounded-2xl border border-neutral-800 bg-neutral-950 px-3 py-2 text-sm text-white outline-none focus:border-cyan-500 sm:w-56"
                                                placeholder="Optional"
                                            />
                                        }
                                        helper={<p className="text-xs text-neutral-500">Saved for your own account view only.</p>}
                                    />
                                    <SettingRow
                                        label="Bio"
                                        right={
                                            <div className="w-full sm:w-64">
                                                <textarea
                                                    rows={3}
                                                    value={form.bio}
                                                    onChange={(event) => setForm((current) => ({ ...current, bio: event.target.value.slice(0, bioLimit) }))}
                                                    maxLength={bioLimit}
                                                    className="w-full resize-none rounded-md border border-neutral-800 bg-neutral-950 px-3 py-2 text-sm text-white outline-none focus:border-cyan-500"
                                                    placeholder="Private note to self..."
                                                />
                                                <div className="mt-1 text-right text-[11px] text-neutral-600">{form.bio.length}/{bioLimit}</div>
                                            </div>
                                        }
                                        helper={<p className="text-xs text-neutral-500">This does not enable public discovery.</p>}
                                    />
                                </Section>
                            )}

                            {canAccessSentinel && canShowSection("sentinel") && (
                                <div className="hidden md:block">
                                    <Section id="settings-sentinel" title="Sentinel" description="Realm oversight and signal review." icon={<ScanQrCode size={18} />} tone="violet" motion="orbit" compact>
                                        <div className="px-4 py-4">
                                            <button
                                                type="button"
                                                onClick={() => navigate("/sentinel")}
                                                className="group relative flex w-full items-center gap-3 overflow-hidden rounded-[1.15rem] bg-violet-950/[0.055] px-3.5 py-3.5 text-left transition hover:bg-violet-950/[0.085]"
                                            >
                                                <span className="pointer-events-none absolute inset-0 opacity-70 [background:radial-gradient(circle_at_0%_0%,rgba(168,85,247,0.16),transparent_42%),linear-gradient(115deg,transparent,rgba(34,211,238,0.08),transparent)]" />
                                                <span className="relative grid h-10 w-10 shrink-0 place-items-center rounded-[0.8rem] border border-violet-300/20 bg-black/45 text-violet-200 shadow-[0_0_24px_rgba(168,85,247,0.14)]">
                                                    <ScanQrCode size={18} />
                                                </span>
                                                <span className="relative min-w-0 flex-1">
                                                    <span className="block text-sm font-black text-violet-100">Open Sentinel</span>
                                                    <span className="mt-1 block truncate text-xs font-semibold text-neutral-500">Blackbox, reports, permissions, and live realm controls.</span>
                                                </span>
                                                <span className="relative text-xs font-black text-violet-200 transition group-hover:translate-x-0.5">
                                                    Open
                                                </span>
                                            </button>
                                        </div>
                                    </Section>
                                </div>
                            )}

                            {/* Profile */}
                            {!isPresence && canShowSection("profile") && (
                                <Section id="settings-profile" title="Account" description="Profile basics and identity." icon={<UserRound size={16} />} tone="cyan" motion="shimmer" compact>
                                    <div className="space-y-4 py-4">
                                        <div className="px-4">
                                            <div className="flex items-start gap-2.5">
                                                <span className="grid h-7 w-7 shrink-0 place-items-center text-neutral-500">
                                                    <UserRound size={15} />
                                                </span>
                                                <div className="min-w-0">
                                                    <div className="text-sm font-semibold text-neutral-100">Avatar</div>
                                                    <p className="mt-1 text-xs font-semibold leading-5 text-neutral-600">Preview only until media storage is connected.</p>
                                                </div>
                                            </div>

                                            <div className="bs-settings-session-card relative mt-3 overflow-hidden rounded-[1.15rem] bg-white/[0.012] px-3.5 py-3.5 transition hover:bg-cyan-950/[0.035]">
                                                <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                                                    <div className="flex min-w-0 items-center gap-3.5">
                                                        <div
                                                            className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden bg-black/60 text-cyan-200 shadow-[0_0_26px_rgba(34,211,238,0.13)] ring-1 ring-cyan-300/15"
                                                            style={{ borderRadius: `${AVATAR_SOFT_RADIUS}px` }}
                                                        >
                                                            {form.avatarUrl ? (
                                                                <img
                                                                    src={form.avatarUrl}
                                                                    alt="Avatar"
                                                                    className="h-full w-full object-cover"
                                                                    onError={() => setForm((f) => ({ ...f, avatarUrl: "" }))}
                                                                />
                                                            ) : (
                                                                <DefaultSilhouette />
                                                            )}
                                                        </div>
                                                        <div className="min-w-0">
                                                            <div className="truncate text-base font-black text-neutral-100">
                                                                {form.displayName || form.username || "Blackspace user"}
                                                            </div>
                                                            <div className="mt-1 truncate text-xs font-semibold text-neutral-500">
                                                                @{form.username || "username"}
                                                            </div>
                                                            <div className="mt-1 text-xs font-semibold text-neutral-600">
                                                                Preview only for now.
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
                                                        <label
                                                            className="group inline-flex cursor-pointer items-center gap-2 rounded-xl bg-cyan-300/10 px-3 py-2 text-xs font-black text-cyan-100 shadow-[0_0_16px_rgba(34,211,238,0.08)] transition hover:bg-cyan-300/15 hover:shadow-[0_0_20px_rgba(34,211,238,0.14)]"
                                                            title="Upload preview"
                                                        >
                                                            <input
                                                                type="file"
                                                                accept={allowedAvatarTypes.join(",")}
                                                                className="hidden"
                                                                onChange={(e) => handleAvatarPick(e.target.files?.[0] || undefined)}
                                                            />
                                                            <ImagePlus size={14} className="transition group-hover:scale-110" />
                                                            Preview
                                                        </label>
                                                        <button
                                                            type="button"
                                                            className="inline-flex items-center gap-2 rounded-xl bg-white/[0.025] px-3 py-2 text-xs font-black text-neutral-400 transition hover:bg-white/[0.05] hover:text-white"
                                                            title="Reset avatar preview"
                                                            onClick={() => {
                                                                setForm((f) => ({
                                                                    ...f,
                                                                    avatarUrl: initial.avatarUrl,
                                                                    avatarFile: null,
                                                                }));
                                                                setAvatarError(null);
                                                                setUploadProgress(0);
                                                            }}
                                                        >
                                                            <RotateCcw size={14} />
                                                            Reset
                                                        </button>
                                                    </div>
                                                </div>

                                                {(avatarError || form.avatarFile) && (
                                                    <div className="relative mt-4 border-t border-white/[0.05] pt-3">
                                                        {avatarError && <p className="text-xs font-semibold text-rose-300">{avatarError}</p>}
                                                        {form.avatarFile && !avatarError && (
                                                            <p className="text-xs font-semibold text-amber-200">Preview selected. Reset before saving.</p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="border-t border-white/[0.06] px-4 pt-4">
                                            <div className="flex items-start gap-2.5">
                                                <span className="grid h-7 w-7 shrink-0 place-items-center text-neutral-500">
                                                    <AtSign size={15} />
                                                </span>
                                                <div className="min-w-0">
                                                    <div className="text-sm font-semibold text-neutral-100">Identity</div>
                                                    <p className="mt-1 text-xs font-semibold leading-5 text-neutral-600">Username, display name, and profile bio.</p>
                                                </div>
                                            </div>
                                            <div className="mt-3 space-y-2.5">

                                                <div className="bs-settings-session-card group relative overflow-hidden rounded-[1.15rem] bg-cyan-950/[0.045] px-3.5 py-3.5 transition hover:bg-cyan-950/[0.065]">
                                                    <div className="relative grid gap-2 sm:grid-cols-[minmax(7.5rem,1fr)_minmax(13rem,17rem)] sm:items-start">
                                                        <div className="min-w-0">
                                                            <div className="text-sm font-semibold text-neutral-100">Username</div>
                                                            <div className="mt-1 flex items-center justify-between gap-3 text-xs font-semibold leading-5">
                                                                <span className="min-w-0 truncate">
                                                                    {!usernameValidation.valid && usernameTouched ? (
                                                                        <span className="text-rose-300">{usernameValidation.error}</span>
                                                                    ) : usernameValidation.valid &&
                                                                        form.username &&
                                                                        String(form.username).trim().toLowerCase() !== String(initial.username).trim().toLowerCase() ? (
                                                                        <span
                                                                            className={classNames(
                                                                                "transition-all duration-300",
                                                                                checkingUser
                                                                                    ? "text-neutral-400"
                                                                                    : usernameAvailable
                                                                                        ? "text-emerald-300"
                                                                                        : "text-rose-300"
                                                                            )}
                                                                        >
                                                                            {checkingUser ? "Checking..." : usernameAvailable ? "Available" : "Not available"}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-neutral-600">Public handle.</span>
                                                                    )}
                                                                </span>
                                                                <span className="shrink-0 text-[11px] text-neutral-600">
                                                                    {form.username.length}/{USERNAME_MAX_LENGTH}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <input
                                                            value={form.username}
                                                            onChange={(e) =>
                                                                setForm((prev) => ({
                                                                    ...prev,
                                                                    username: e.target.value.replace(/\s+/g, "").slice(0, USERNAME_MAX_LENGTH),
                                                                }))
                                                            }
                                                            maxLength={USERNAME_MAX_LENGTH}
                                                            autoComplete="username"
                                                            onFocus={() => setUsernameTouched(true)}
                                                            placeholder="your_handle"
                                                            className={accountInputClass}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="bs-settings-session-card group relative overflow-hidden rounded-[1.15rem] bg-white/[0.012] px-3.5 py-3.5 transition hover:bg-cyan-950/[0.035]">
                                                    <div className="relative grid gap-2 sm:grid-cols-[minmax(7.5rem,1fr)_minmax(13rem,17rem)] sm:items-start">
                                                        <div className="min-w-0">
                                                            <div className="text-sm font-semibold text-neutral-100">Display name</div>
                                                            <p className="mt-1 text-xs font-semibold leading-5 text-neutral-600">Shown beside your profile.</p>
                                                        </div>
                                                        <input
                                                            value={form.displayName}
                                                            onChange={(e) =>
                                                                setForm((p) => ({
                                                                    ...p,
                                                                    displayName: e.target.value.slice(0, 50),
                                                                }))
                                                            }
                                                            placeholder="Your name"
                                                            className={accountInputClass}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="bs-settings-session-card group relative overflow-hidden rounded-[1.15rem] bg-white/[0.012] px-3.5 py-3.5 transition hover:bg-cyan-950/[0.035]">
                                                    <div className="relative space-y-2.5">
                                                        <div className="flex items-center justify-between gap-3">
                                                            <span className="min-w-0">
                                                                <span className="block text-sm font-semibold text-neutral-100">Bio</span>
                                                                <span className="mt-1 block text-xs font-semibold leading-5 text-neutral-600">Short public profile note.</span>
                                                            </span>
                                                            <span
                                                                className={classNames(
                                                                    "shrink-0 text-[11px] font-bold tabular-nums transition-colors",
                                                                    form.bio.length >= bioLimit ? "text-rose-300" : "text-neutral-600"
                                                                )}
                                                            >
                                                                {form.bio.length}/{bioLimit}
                                                            </span>
                                                        </div>
                                                        <textarea
                                                            value={form.bio}
                                                            onChange={(e) =>
                                                                setForm((p) => ({
                                                                    ...p,
                                                                    bio: e.target.value.slice(0, bioLimit),
                                                                }))
                                                            }
                                                            rows={3}
                                                            placeholder="Short profile note"
                                                            className={`${accountBioInputClass} min-h-24 resize-none`}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                </Section>

                            )}

                            {/* Privacy */}
                            {!isPresence && canShowSection("privacy") && (
                                <Section id="settings-privacy" expandSignal={targetSectionId === "settings-privacy" ? sectionExpandSignal : undefined} title="Privacy" description="Visibility, reachability, and online status." icon={<EyeOff size={18} />} tone="violet" motion="orbit" compact>
                                    <div className="space-y-4 py-4">
                                        <div className="px-4">
                                            <div className="flex items-start gap-2.5">
                                                <span className="grid h-7 w-7 shrink-0 place-items-center text-neutral-500">
                                                    <Search size={15} />
                                                </span>
                                                <span className="min-w-0">
                                                    <span className="block text-sm font-semibold text-neutral-100">Visibility</span>
                                                    <span className="mt-1 block text-xs font-semibold leading-5 text-neutral-600">Profile, search, activity, and online controls.</span>
                                                </span>
                                            </div>
                                            <div className="mt-3 space-y-2.5">
                                                {[
                                                    {
                                                        label: "Account",
                                                        helper: "How open your profile is.",
                                                        right: renderChoice<"public" | "private">(
                                                            form.accountVisibility,
                                                            (accountVisibility) => setForm((p) => ({ ...p, accountVisibility })),
                                                            ["public", "private"],
                                                            { public: "Open", private: "Private" }
                                                        ),
                                                    },
                                                    {
                                                        label: "Search",
                                                        helper: "Who can find you.",
                                                        right: renderChoice<AudienceVisibility>(
                                                            form.searchVisibility,
                                                            (searchVisibility) => setForm((p) => ({ ...p, searchVisibility })),
                                                            ["Everyone", "Friends", "Nobody"]
                                                        ),
                                                    },
                                                    {
                                                        label: "Activity",
                                                        helper: "Who can see your activity.",
                                                        right: renderChoice<AudienceVisibility>(
                                                            form.activityVisibility,
                                                            (activityVisibility) => setForm((p) => ({ ...p, activityVisibility })),
                                                            ["Everyone", "Friends", "Nobody"]
                                                        ),
                                                    },
                                                    {
                                                        label: "Online",
                                                        helper: form.incognitoMode ? "Hidden by Incognito." : "Who can see your online state.",
                                                        right: renderChoice<AudienceVisibility>(
                                                            form.presenceVisibility,
                                                            (presenceVisibility) => setForm((p) => ({ ...p, presenceVisibility })),
                                                            ["Everyone", "Friends", "Nobody"],
                                                            {},
                                                            form.incognitoMode
                                                        ),
                                                    },
                                                ].map((item) => renderPrivacyControlRow(item))}
                                            </div>
                                        </div>

                                        <div className="border-t border-white/[0.06] px-4 pt-4">
                                            <div className="flex items-start gap-2.5">
                                                <span className="grid h-7 w-7 shrink-0 place-items-center text-neutral-500">
                                                    <ShieldOff size={15} />
                                                </span>
                                                <span className="min-w-0">
                                                    <span className="block text-sm font-semibold text-neutral-100">Stealth</span>
                                                    <span className={classNames("mt-1 block text-xs font-semibold leading-5", form.incognitoMode ? "text-teal-300" : "text-neutral-600")}>
                                                        {form.incognitoMode ? "Incognito is active." : "Hide online status, receipts, and contact routes."}
                                                    </span>
                                                </span>
                                            </div>
                                            <div className="mt-3 space-y-2.5">
                                                <div className="group relative overflow-hidden rounded-[1.15rem] bg-white/[0.012] px-3.5 py-3 transition hover:bg-violet-950/[0.035]">
                                                    <div className="relative flex items-center justify-between gap-3">
                                                            <span className="min-w-0">
                                                                <span className="block text-sm font-semibold text-neutral-100">Incognito</span>
                                                                <span className="mt-1 block text-xs font-semibold leading-5 text-neutral-600">Master privacy shield.</span>
                                                            </span>
                                                        {killSwitchLoading ? (
                                                            <div className="grid h-7 w-12 place-items-center">
                                                                <div className="h-4 w-4 animate-spin rounded-full border-2 border-teal-400 border-t-transparent" />
                                                            </div>
                                                        ) : (
                                                            <Toggle
                                                                ariaLabel="Incognito Mode"
                                                                checked={form.incognitoMode}
                                                                onChange={async () => {
                                                                    setKillSwitchLoading(true);
                                                                    try {
                                                                        const res = await toggleKillSwitch();
                                                                        if (!res.success) throw new Error(res.message);

                                                                        localStorage.setItem('globalIncognito', res.killSwitch ? 'true' : 'false');
                                                                        window.dispatchEvent(new StorageEvent('storage', { key: 'globalIncognito', newValue: res.killSwitch ? 'true' : 'false' }));

                                                                        const srv: any = res.user ?? {};
                                                                        const pick = (k: string, def: any) => srv.settings?.[k] !== undefined ? srv.settings[k] : def;
                                                                        const b = (v: any, d = false) => typeof v === 'boolean' ? v : Boolean(v ?? d);
                                                                        setForm(prev => ({
                                                                            ...prev,
                                                                            incognitoMode: b(pick('incognitoMode', pick('killSwitch', false))),
                                                                            showOnline:    b(pick('showOnline',    true), true),
                                                                            lastSeen:      (pick('lastSeen', 'Everyone') as LastSeenVisibility),
                                                                            readReceipts:  b(pick('readReceipts',  true), true),
                                                                            whoCanMessage: normalizeMessageReach(pick('whoCanMessage', 'Anyone')),
                                                                            whoCanInviteGroups: normalizeMessageReach(pick('whoCanInviteGroups', 'Anyone')),
                                                                        }));
                                                                        setInitial(prev => ({
                                                                            ...prev,
                                                                            incognitoMode: b(pick('incognitoMode', pick('killSwitch', false))),
                                                                            showOnline:    b(pick('showOnline',    true), true),
                                                                            lastSeen:      (pick('lastSeen', 'Everyone') as LastSeenVisibility),
                                                                            readReceipts:  b(pick('readReceipts',  true), true),
                                                                            whoCanMessage: normalizeMessageReach(pick('whoCanMessage', 'Anyone')),
                                                                            whoCanInviteGroups: normalizeMessageReach(pick('whoCanInviteGroups', 'Anyone')),
                                                                        }));
                                                                        showNotice("ok", res.message ?? "Incognito updated.");
                                                                    } catch (e: any) {
                                                                        showNotice("err", e?.message ?? "Failed to toggle Incognito");
                                                                    } finally {
                                                                        setKillSwitchLoading(false);
                                                                    }
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                </div>

                                                {[
                                                    {
                                                        label: "Online",
                                                        helper: form.incognitoMode ? "Hidden by Incognito." : "Show active status.",
                                                        right: (
                                                            <Toggle
                                                                ariaLabel="Show Online"
                                                                checked={form.showOnline}
                                                                onChange={() => setForm((p) => ({ ...p, showOnline: !p.showOnline }))}
                                                                disabled={form.incognitoMode}
                                                            />
                                                        ),
                                                    },
                                                    {
                                                        label: "Last seen",
                                                        helper: form.incognitoMode ? "Hidden by Incognito." : "Chat last-seen visibility.",
                                                        right: renderChoice<LastSeenVisibility>(
                                                            form.lastSeen,
                                                            (lastSeen) => setForm((p) => ({ ...p, lastSeen })),
                                                            ["Everyone", "Contacts", "Nobody"],
                                                            {},
                                                            form.incognitoMode
                                                        ),
                                                    },
                                                    {
                                                        label: "Receipts",
                                                        helper: form.incognitoMode ? "Off under Incognito." : "Send read confirmations.",
                                                        right: (
                                                            <Toggle
                                                                ariaLabel="Read Receipts"
                                                                checked={form.readReceipts}
                                                                onChange={() => setForm((p) => ({ ...p, readReceipts: !p.readReceipts }))}
                                                                disabled={form.incognitoMode}
                                                            />
                                                        ),
                                                    },
                                                ].map((item) => renderPrivacyControlRow({ ...item, muted: form.incognitoMode }))}
                                            </div>
                                        </div>

                                        <div className="border-t border-white/[0.06] px-4 pt-4">
                                            <div className="flex items-start gap-2.5">
                                                <span className="grid h-7 w-7 shrink-0 place-items-center text-neutral-500">
                                                    <Users size={15} />
                                                </span>
                                                <span className="min-w-0">
                                                    <span className="block text-sm font-semibold text-neutral-100">Reachability</span>
                                                    <span className="mt-1 block text-xs font-semibold leading-5 text-neutral-600">Who can start contact or invite you.</span>
                                                </span>
                                            </div>
                                            <div className="mt-3 space-y-2.5">
                                                {[
                                                    {
                                                        label: "Messages",
                                                        helper: form.incognitoMode ? "Blocked by Incognito." : "Who can start a message.",
                                                        right: renderChoice<MessageReach>(
                                                            form.whoCanMessage,
                                                            (whoCanMessage) => setForm((p) => ({ ...p, whoCanMessage })),
                                                            ["Anyone", "Friends", "No One"],
                                                            { "No One": "None" },
                                                            form.incognitoMode
                                                        ),
                                                    },
                                                    {
                                                        label: "Space invites",
                                                        helper: form.incognitoMode ? "Blocked by Incognito." : "Who can invite you.",
                                                        right: renderChoice<MessageReach>(
                                                            form.whoCanInviteGroups,
                                                            (whoCanInviteGroups) => setForm((p) => ({ ...p, whoCanInviteGroups })),
                                                            ["Anyone", "Friends", "No One"],
                                                            { "No One": "None" },
                                                            form.incognitoMode
                                                        ),
                                                    },
                                                ].map((item) => renderPrivacyControlRow({ ...item, muted: form.incognitoMode }))}
                                            </div>
                                        </div>
                                    </div>
                                </Section>
                            )}

                            {/* Security */}
                            {canShowSection("security") && (
                                <Section id="settings-security" title="Security" description="Password, 2FA, and phrase recovery." icon={<LockKeyhole size={18} />} tone="emerald" motion="scan" compact>
                                    <div className="border-b border-white/[0.06] px-4 py-3.5">
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="flex min-w-0 items-center gap-2.5">
                                                <span className="grid h-7 w-7 shrink-0 place-items-center text-neutral-500">
                                                    <KeyRound size={15} />
                                                </span>
                                                <span className="min-w-0">
                                                    <span className="block truncate text-sm font-semibold text-neutral-100">
                                                        Password
                                                    </span>
                                                    <span className="mt-1 block truncate text-xs font-semibold text-neutral-600">
                                                        Change your account password.
                                                    </span>
                                                </span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => recoveryConfigured ? setPasswordDialogOpen(true) : openRecoveryModal()}
                                                className={tinyButtonClass}
                                            >
                                                {recoveryConfigured ? "Change" : "Set phrase first"}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="border-b border-white/[0.06] px-4 py-3.5">
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="flex min-w-0 items-center gap-2.5">
                                                <span className="grid h-7 w-7 shrink-0 place-items-center text-neutral-500">
                                                    <ScanQrCode size={15} />
                                                </span>
                                                <span className="min-w-0">
                                                    <span className="block truncate text-sm font-semibold text-neutral-100">
                                                        2FA
                                                    </span>
                                                    <span className="mt-1 block truncate text-xs font-semibold text-neutral-600">
                                                        {twoFactorConfigured ? "Setup profile saved." : "Authenticator setup preview."}
                                                    </span>
                                                </span>
                                            </div>
                                            <button type="button" onClick={openTwoFactorModal} className={tinyButtonClass}>
                                                {twoFactorConfigured ? "Modify" : "Set up"}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="px-4 py-3.5">
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="flex min-w-0 items-center gap-2.5">
                                                <span className="grid h-7 w-7 shrink-0 place-items-center text-neutral-500">
                                                    <LifeBuoy size={15} />
                                                </span>
                                                <span className="min-w-0">
                                                    <span className="block truncate text-sm font-semibold text-neutral-100">
                                                        Recovery phrase
                                                    </span>
                                                    <span className="mt-1 block truncate text-xs font-semibold text-neutral-600">
                                                        {recoveryConfigured
                                                            ? "Active and required for password changes."
                                                            : recoveryPhraseStatus === "used"
                                                                ? "Used during recovery. Create a replacement."
                                                                : "Secure password recovery without email."}
                                                    </span>
                                                </span>
                                            </div>
                                            <button type="button" onClick={openRecoveryModal} className={tinyButtonClass}>
                                                {recoveryConfigured ? "Modify" : "Set up"}
                                            </button>
                                        </div>
                                    </div>
                                </Section>
                            )}

                            {/* Notifications */}
                            {canShowSection("notifications") && (
                                <Section id="settings-notifications" expandSignal={targetSectionId === "settings-notifications" ? sectionExpandSignal : undefined} title="Notifications" description="Control signals, sounds, and alerts." icon={<Bell size={18} />} tone="amber" motion="ember" compact>
                                    <div className="space-y-4 py-4">
                                        <div className="px-4">
                                            <div className="flex items-start gap-2.5">
                                                <span className="grid h-7 w-7 shrink-0 place-items-center text-neutral-500">
                                                    <Bell size={15} />
                                                </span>
                                                <div className="min-w-0">
                                                    <div className="text-sm font-semibold text-neutral-100">Delivery</div>
                                                    <p className="mt-1 text-xs font-semibold leading-5 text-neutral-600">Choose how Blackspace reaches you.</p>
                                                </div>
                                            </div>
                                            <div
                                                className={classNames(
                                                    "bs-settings-session-card relative mt-3 overflow-hidden rounded-[1.15rem] p-3.5 transition",
                                                    [form.pushNotifications, form.emailNotifications, form.soundEffects].some(Boolean)
                                                        ? "bg-amber-950/[0.055] shadow-[0_0_22px_rgba(250,204,21,0.045)]"
                                                        : "bg-white/[0.012] hover:bg-amber-950/[0.04]"
                                                )}
                                            >
                                                <div className="relative grid grid-cols-3 gap-2">
                                                    {[
                                                        {
                                                            label: "Push",
                                                            helper: "In-app",
                                                            icon: <BellRing size={16} />,
                                                            checked: form.pushNotifications,
                                                            aria: "Push Notifications",
                                                            onChange: togglePushNotifications,
                                                        },
                                                        {
                                                            label: "Email",
                                                            helper: "Inbox",
                                                            icon: <Mail size={16} />,
                                                            checked: form.emailNotifications,
                                                            aria: "Email Notifications",
                                                            onChange: () => setForm((p) => ({ ...p, emailNotifications: !p.emailNotifications })),
                                                        },
                                                        {
                                                            label: "Sound",
                                                            helper: "Chime",
                                                            icon: <Volume2 size={16} />,
                                                            checked: form.soundEffects,
                                                            aria: "Sound Effects",
                                                            onChange: () => setForm((p) => ({ ...p, soundEffects: !p.soundEffects })),
                                                        },
                                                    ].map((item) => (
                                                        <button
                                                            key={item.label}
                                                            type="button"
                                                            aria-label={item.aria}
                                                            aria-pressed={item.checked}
                                                            onClick={item.onChange}
                                                            className={classNames(
                                                                "group relative overflow-hidden rounded-[1rem] px-2.5 py-3 text-left transition",
                                                                item.checked
                                                                    ? "bg-amber-300/10 text-amber-50 shadow-[0_0_22px_rgba(250,204,21,0.12)]"
                                                                    : "bg-white/[0.018] text-neutral-500 hover:bg-white/[0.04] hover:text-neutral-200"
                                                            )}
                                                        >
                                                            <span className={classNames("mb-2 grid h-8 w-8 place-items-center rounded-[0.75rem] transition", item.checked ? "bg-amber-300/12 text-amber-100" : "bg-black/45 text-neutral-500 group-hover:text-amber-100")}>
                                                                {item.icon}
                                                            </span>
                                                            <span className="block truncate text-xs font-black">{item.label}</span>
                                                            <span className="mt-0.5 block truncate text-[10px] font-bold text-neutral-600">{item.helper}</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="space-y-4 px-4">
                                            <div className="border-t border-white/[0.06] pt-4">
                                                <div className="flex items-start gap-2.5">
                                                    <span className="grid h-7 w-7 shrink-0 place-items-center text-neutral-500">
                                                        <Clock3 size={15} />
                                                    </span>
                                                    <span className="min-w-0">
                                                        <span className="block text-sm font-semibold text-neutral-100">Activity</span>
                                                        <span className="mt-1 block text-xs font-semibold leading-5 text-neutral-600">Chats, mentions, and reactions.</span>
                                                    </span>
                                                </div>
                                                <div className="mt-3 space-y-2.5">
                                                    {[
                                                        {
                                                            label: "Messages",
                                                            helper: "Direct chats and requests.",
                                                            icon: <MessageCircle size={15} />,
                                                            checked: form.notifyMessages,
                                                            aria: "Message notifications",
                                                            onChange: () => setForm((p) => ({ ...p, notifyMessages: !p.notifyMessages })),
                                                        },
                                                        {
                                                            label: "Mentions",
                                                            helper: "When someone calls your name.",
                                                            icon: <AtSign size={15} />,
                                                            checked: form.notifyMentions,
                                                            aria: "Mention notifications",
                                                            onChange: () => setForm((p) => ({ ...p, notifyMentions: !p.notifyMentions })),
                                                        },
                                                        {
                                                            label: "Reactions",
                                                            helper: "Replies, likes, and responses.",
                                                            icon: <Heart size={15} />,
                                                            checked: form.notifyReactions,
                                                            aria: "Reaction notifications",
                                                            onChange: () => setForm((p) => ({ ...p, notifyReactions: !p.notifyReactions })),
                                                        },
                                                    ].map((item) => (
                                                        <div
                                                            key={item.label}
                                                            className={classNames(
                                                                "group relative overflow-hidden rounded-[1.15rem] px-3.5 py-3 transition",
                                                                item.checked
                                                                    ? "bg-amber-950/[0.055] shadow-[0_0_22px_rgba(250,204,21,0.055)]"
                                                                    : "bg-white/[0.012] hover:bg-amber-950/[0.035]"
                                                            )}
                                                        >
                                                            <div className="relative flex items-center justify-between gap-3">
                                                                <div className="flex min-w-0 items-center gap-2.5">
                                                                    <span className={classNames("grid h-7 w-7 shrink-0 place-items-center transition", item.checked ? "text-amber-100 drop-shadow-[0_0_9px_rgba(250,204,21,0.28)]" : "text-neutral-500 group-hover:text-neutral-300")}>
                                                                        {item.icon}
                                                                    </span>
                                                                    <span className="min-w-0">
                                                                        <span className="block truncate text-sm font-semibold text-neutral-100">{item.label}</span>
                                                                        <span className="mt-1 block truncate text-xs font-semibold text-neutral-600">{item.helper}</span>
                                                                    </span>
                                                                </div>
                                                                <Toggle ariaLabel={item.aria} checked={item.checked} onChange={item.onChange} />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            <div className="border-t border-white/[0.06] pt-4">
                                                <div className="flex items-start gap-2.5">
                                                    <span className="grid h-7 w-7 shrink-0 place-items-center text-neutral-500">
                                                        <LockKeyhole size={15} />
                                                    </span>
                                                    <span className="min-w-0">
                                                        <span className="block text-sm font-semibold text-neutral-100">Spaces & Vault</span>
                                                        <span className="mt-1 block text-xs font-semibold leading-5 text-neutral-600">Community and private knowledge updates.</span>
                                                    </span>
                                                </div>
                                                <div className="mt-3 space-y-2.5">
                                                    {[
                                                        {
                                                            label: "Spaces",
                                                            helper: "Community movement.",
                                                            icon: <Users size={15} />,
                                                            checked: form.notifySpaces,
                                                            aria: "Space notifications",
                                                            onChange: () => setForm((p) => ({ ...p, notifySpaces: !p.notifySpaces })),
                                                        },
                                                        {
                                                            label: "Vault sharing",
                                                            helper: "Shared notes and access.",
                                                            icon: <Vault size={15} />,
                                                            checked: form.notifyVault,
                                                            aria: "Vault notifications",
                                                            onChange: () => setForm((p) => ({ ...p, notifyVault: !p.notifyVault })),
                                                        },
                                                    ].map((item) => (
                                                        <div
                                                            key={item.label}
                                                            className={classNames(
                                                                "group relative overflow-hidden rounded-[1.15rem] px-3.5 py-3 transition",
                                                                item.checked
                                                                    ? "bg-amber-950/[0.055] shadow-[0_0_22px_rgba(250,204,21,0.055)]"
                                                                    : "bg-white/[0.012] hover:bg-amber-950/[0.035]"
                                                            )}
                                                        >
                                                            <div className="relative flex items-center justify-between gap-3">
                                                                <div className="flex min-w-0 items-center gap-2.5">
                                                                    <span className={classNames("grid h-7 w-7 shrink-0 place-items-center transition", item.checked ? "text-amber-100 drop-shadow-[0_0_9px_rgba(250,204,21,0.28)]" : "text-neutral-500 group-hover:text-neutral-300")}>
                                                                        {item.icon}
                                                                    </span>
                                                                    <span className="min-w-0">
                                                                        <span className="block truncate text-sm font-semibold text-neutral-100">{item.label}</span>
                                                                        <span className="mt-1 block truncate text-xs font-semibold text-neutral-600">{item.helper}</span>
                                                                    </span>
                                                                </div>
                                                                <Toggle ariaLabel={item.aria} checked={item.checked} onChange={item.onChange} />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            <div className="border-t border-white/[0.06] pt-4">
                                                <div className="flex items-start gap-2.5">
                                                    <span className="grid h-7 w-7 shrink-0 place-items-center text-neutral-500">
                                                        <UserRound size={15} />
                                                    </span>
                                                    <span className="min-w-0">
                                                        <span className="block text-sm font-semibold text-neutral-100">Account</span>
                                                        <span className="mt-1 block text-xs font-semibold leading-5 text-neutral-600">Security and account events.</span>
                                                    </span>
                                                </div>
                                                <div className={classNames("group relative mt-3 overflow-hidden rounded-[1.15rem] px-3.5 py-3 transition", form.notifySecurity ? "bg-amber-950/[0.055] shadow-[0_0_22px_rgba(250,204,21,0.055)]" : "bg-white/[0.012] hover:bg-amber-950/[0.035]")}>
                                                    <div className="relative flex items-center justify-between gap-3">
                                                        <div className="flex min-w-0 items-center gap-2.5">
                                                            <span className={classNames("grid h-7 w-7 shrink-0 place-items-center transition", form.notifySecurity ? "text-amber-100 drop-shadow-[0_0_9px_rgba(250,204,21,0.28)]" : "text-neutral-500 group-hover:text-neutral-300")}>
                                                                <KeyRound size={15} />
                                                            </span>
                                                            <span className="min-w-0">
                                                                <span className="block truncate text-sm font-semibold text-neutral-100">Security alerts</span>
                                                                <span className="mt-1 block truncate text-xs font-semibold text-neutral-600">Password, recovery, and account events.</span>
                                                            </span>
                                                        </div>
                                                        <Toggle ariaLabel="Security notifications" checked={form.notifySecurity} onChange={() => setForm((p) => ({ ...p, notifySecurity: !p.notifySecurity }))} />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Section>
                            )}

                            {/* Appearance */}
                            {canShowSection("appearance") && (
                                <Section id="settings-appearance" expandSignal={targetSectionId === "settings-appearance" ? sectionExpandSignal : undefined} title="Appearance" description="Theme, motion, and readability." icon={<Palette size={18} />} tone="cyan" motion="shimmer" compact>
                                    <div className="space-y-3 px-4 py-4">
                                        <div className="rounded-[1.15rem] bg-white/[0.025] p-3">
                                            <div className="flex items-center justify-between gap-3">
                                                <div>
                                                    <div className="text-sm font-semibold text-neutral-100">
                                                        Theme <span className="text-cyan-200">: {form.theme}</span>
                                                    </div>
                                                    <p className="mt-1 text-xs leading-5 text-neutral-500">
                                                        Keep your preferred skins close.
                                                    </p>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => setAppearanceModal("themes")}
                                                    className="shrink-0 rounded-xl bg-cyan-300/10 px-3 py-2 text-xs font-black text-cyan-100 transition hover:bg-cyan-300/15 hover:shadow-[0_0_16px_rgba(34,211,238,0.16)]"
                                                >
                                                    Manage
                                                </button>
                                            </div>
                                            <div className="mt-3 space-y-2.5">
                                                {visibleThemeGroups.map((group) => (
                                                    <div key={group.title} className="grid gap-2 sm:grid-cols-[4.5rem_1fr] sm:items-center">
                                                        <div className="text-[10px] font-black uppercase tracking-[0.22em] text-neutral-500">
                                                            {group.title}
                                                        </div>
                                                        <div className="flex flex-wrap gap-1.5">
                                                            {group.themes.map((themeName) => {
                                                                const theme = THEME_OPTIONS.find((option) => option.name === themeName);
                                                                if (!theme) return null;
                                                                const selected = form.theme === theme.name;
                                                                return (
                                                                    <button
                                                                        key={theme.name}
                                                                        type="button"
                                                                        title={`${theme.name} - ${theme.bestFor}`}
                                                                        onClick={() => selectTheme(theme.name)}
                                                                        aria-pressed={selected}
                                                                        className={classNames(
                                                                            "bs-theme-tile h-10 w-10 rounded-[0.8rem] border p-1.5 transition sm:h-9 sm:w-9",
                                                                            selected
                                                                                ? "border-cyan-200/55 bg-cyan-300/16 shadow-[0_0_24px_rgba(34,211,238,0.42),inset_0_0_14px_rgba(34,211,238,0.16)]"
                                                                                : "border-white/[0.07] bg-white/[0.045] shadow-[inset_0_0_16px_rgba(255,255,255,0.025)] hover:bg-white/[0.07]"
                                                                        )}
                                                                    >
                                                                        {renderThemeSwatches(theme, "grid h-full place-content-center grid-cols-2 gap-0.5")}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="grid gap-3 sm:grid-cols-2">
                                            <div className="rounded-[1.15rem] bg-white/[0.025] p-3">
                                                <div className="text-sm font-semibold text-neutral-100">Font Size</div>
                                                <div className="mt-3 grid grid-cols-3 gap-2">
                                                    {(["Small", "Medium", "Large"] as const).map((fontSize) => (
                                                        <button
                                                            key={fontSize}
                                                            type="button"
                                                            onClick={() => applyAppearanceDraft({ fontSize })}
                                                            className={classNames(
                                                                "rounded-xl px-3 py-2 text-xs font-black transition",
                                                                form.fontSize === fontSize
                                                                    ? "bg-cyan-300/14 text-cyan-100 shadow-[0_0_18px_rgba(34,211,238,0.10)]"
                                                                    : "bg-black/35 text-neutral-400 hover:text-white"
                                                            )}
                                                        >
                                                            {fontSize}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            <div className="rounded-[1.15rem] bg-white/[0.025] p-3">
                                                <div className="flex items-start justify-between gap-3">
                                                    <div>
                                                        <div className="text-sm font-semibold text-neutral-100">
                                                            Language <span className="text-emerald-200">: {form.language}</span>
                                                        </div>
                                                        <p className="mt-1 text-xs leading-5 text-neutral-500">
                                                            Choose which codes stay visible.
                                                        </p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => setAppearanceModal("languages")}
                                                        className="shrink-0 rounded-xl bg-emerald-300/10 px-3 py-2 text-xs font-black text-emerald-100 transition hover:bg-emerald-300/15 hover:shadow-[0_0_16px_rgba(110,231,183,0.16)]"
                                                    >
                                                        Manage
                                                    </button>
                                                </div>
                                                <div className="mt-3 grid grid-cols-7 gap-1.5">
                                                    {visibleLanguageCodes.map((code) => {
                                                        const language = LANGUAGE_OPTIONS.find((option) => option.code === code);
                                                        if (!language) return null;
                                                        const selected = form.language === language.code;
                                                        return (
                                                            <button
                                                                key={language.code}
                                                                type="button"
                                                                title={language.label}
                                                                aria-pressed={selected}
                                                                onClick={() => selectLanguage(language.code)}
                                                                className={classNames(
                                                                    "grid h-10 place-items-center rounded-xl text-xs font-black transition",
                                                                    selected
                                                                        ? "bg-emerald-300/12 text-emerald-50 shadow-[0_0_18px_rgba(110,231,183,0.22)]"
                                                                        : "bg-black/30 text-neutral-500 hover:bg-white/[0.04] hover:text-white"
                                                                )}
                                                            >
                                                                {language.code}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="rounded-[1.15rem] bg-white/[0.025] p-3">
                                            <div className="flex items-start justify-between gap-3">
                                                <div>
                                                    <div className="text-sm font-semibold text-neutral-100">Effects</div>
                                                    <p className="mt-1 text-xs leading-5 text-neutral-500">
                                                        Glow, motion, and contrast controls.
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="mt-3 grid grid-cols-3 gap-2">
                                                <button
                                                    type="button"
                                                    aria-pressed={form.glow}
                                                    onClick={() => applyAppearanceDraft({ glow: !form.glow })}
                                                    className={classNames(
                                                        "grid h-12 place-items-center rounded-xl px-2.5 text-center text-xs font-black transition",
                                                        form.glow
                                                            ? "bg-cyan-300/12 text-cyan-100 shadow-[0_0_18px_rgba(34,211,238,0.12)]"
                                                            : "bg-black/35 text-neutral-500 hover:text-white"
                                                    )}
                                                >
                                                    Glow
                                                </button>
                                                <button
                                                    type="button"
                                                    aria-pressed={!form.reduceMotion}
                                                    onClick={() => applyAppearanceDraft({ reduceMotion: !form.reduceMotion })}
                                                    className={classNames(
                                                        "grid h-12 place-items-center rounded-xl px-2.5 text-center text-xs font-black transition",
                                                        !form.reduceMotion
                                                            ? "bg-cyan-300/12 text-cyan-100 shadow-[0_0_18px_rgba(34,211,238,0.12)]"
                                                            : "bg-black/35 text-neutral-500 hover:text-white"
                                                    )}
                                                >
                                                    Motion
                                                </button>
                                                <button
                                                    type="button"
                                                    aria-pressed={form.highContrast}
                                                    onClick={() => applyAppearanceDraft({ highContrast: !form.highContrast })}
                                                    className={classNames(
                                                        "grid h-12 place-items-center rounded-xl px-2.5 text-center text-xs font-black transition",
                                                        form.highContrast
                                                            ? "bg-cyan-300/12 text-cyan-100 shadow-[0_0_18px_rgba(34,211,238,0.12)]"
                                                            : "bg-black/35 text-neutral-500 hover:text-white"
                                                    )}
                                                >
                                                    Contrast
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </Section>
                            )}

                            {/* Danger Zone */}
                            {!isPresence && canShowSection("danger") && (
                                <Section
                                    id="settings-danger"
                                    title="Danger"
                                    description="Irreversible account actions."
                                    icon={<ShieldAlert size={18} />}
                                    tone="rose"
                                    motion="ember"
                                    compact
                                >
                                    <h2 className="hidden">
                                        Danger Zone
                                    </h2>
                                    <div className="space-y-4">
                                        <div className="relative overflow-hidden bg-transparent px-4 py-3.5">
                                            <div className="relative">
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="flex min-w-0 items-start gap-2.5">
                                                        <span className="mt-[1px] grid h-5 w-5 shrink-0 place-items-center text-neutral-500">
                                                            <Laptop size={15} />
                                                        </span>
                                                        <div className="min-w-0">
                                                            <div className="leading-5 text-sm font-semibold text-neutral-100">Logged Devices</div>
                                                            <p className="mt-1.5 max-w-xl text-xs leading-5 text-neutral-500">
                                                                Review active devices.
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex shrink-0 items-center gap-3">
                                                        <button
                                                            type="button"
                                                            onClick={() => loadAccountSessions(false)}
                                                            disabled={sessionsLoading}
                                                            className="group text-rose-200 transition hover:text-rose-50 hover:drop-shadow-[0_0_10px_rgba(244,63,94,0.55)] disabled:opacity-50"
                                                            title="Refresh"
                                                            aria-label="Refresh"
                                                        >
                                                            <RefreshCw size={18} className={sessionsLoading ? "animate-spin" : "transition group-hover:rotate-45"} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={confirmLogoutAllSessions}
                                                            className="text-fuchsia-200 transition hover:text-fuchsia-50 hover:drop-shadow-[0_0_11px_rgba(217,70,239,0.6)]"
                                                            title="Log out all sessions"
                                                            aria-label="Log out all sessions"
                                                        >
                                                            <LogOut size={18} />
                                                        </button>
                                                    </div>
                                                </div>

                                                {sessionsError && (
                                                    <p className="mt-4 rounded-2xl border border-rose-400/20 bg-rose-950/25 px-3 py-2 text-xs font-bold text-rose-100">
                                                        {sessionsError}
                                                    </p>
                                                )}

                                                <div className="mt-4 space-y-2.5">
                                                    {sessionsLoading && accountSessions.length === 0 && (
                                                        <div className="rounded-[1.15rem] border border-white/[0.06] bg-white/[0.025] px-4 py-4">
                                                            <div className="flex items-center gap-3">
                                                                <span className="h-10 w-10 animate-pulse rounded-2xl bg-white/[0.06]" />
                                                                <span className="min-w-0 flex-1 space-y-2">
                                                                    <span className="block h-3 w-32 animate-pulse rounded-full bg-white/[0.08]" />
                                                                    <span className="block h-2.5 w-44 max-w-full animate-pulse rounded-full bg-white/[0.05]" />
                                                                </span>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {!sessionsLoading && accountSessions.length === 0 && (
                                                        <div className="rounded-[1.15rem] border border-white/[0.06] bg-white/[0.025] px-4 py-4 text-xs font-semibold leading-5 text-neutral-500">
                                                            No active sessions yet. Log in from another browser or device and it will appear here.
                                                        </div>
                                                    )}

                                                    {accountSessions.map((session) => (
                                                        <div
                                                            key={session.id}
                                                            className={classNames(
                                                                "bs-settings-session-card group relative overflow-hidden rounded-[1.15rem] px-3.5 py-3 transition",
                                                                session.current
                                                                    ? "bg-rose-950/[0.055] shadow-[0_0_22px_rgba(244,63,94,0.045)]"
                                                                    : "bg-white/[0.012] hover:bg-rose-950/[0.04]"
                                                            )}
                                                        >
                                                            <div className="flex items-start gap-3">
                                                                <div
                                                                    className={classNames(
                                                                        "flex w-12 shrink-0",
                                                                        session.current
                                                                            ? "flex-col items-center pt-[3px]"
                                                                            : "items-start justify-center pt-[1px]"
                                                                    )}
                                                                >
                                                                    {session.current && (
                                                                        <span className="mb-1 text-[8px] font-black uppercase leading-4 tracking-[0.16em] text-rose-100 drop-shadow-[0_0_8px_rgba(244,63,94,0.35)]">
                                                                            Live
                                                                        </span>
                                                                    )}
                                                                    <div
                                                                        className={classNames(
                                                                            "grid h-6 w-6 place-items-center transition",
                                                                            session.current
                                                                                ? "text-rose-100 drop-shadow-[0_0_10px_rgba(244,63,94,0.24)]"
                                                                                : "text-neutral-300 group-hover:text-rose-100"
                                                                        )}
                                                                    >
                                                                        {sessionDeviceIcon(session)}
                                                                    </div>
                                                                </div>
                                                                <div className="min-w-0 flex-1">
                                                                    <div className="flex min-w-0 items-center gap-2 leading-4">
                                                                        <span className="truncate text-sm font-black text-white">
                                                                            {session.deviceLabel || "Unknown device"}
                                                                        </span>
                                                                    </div>
                                                                    <div className="-ml-1 mt-1.5 flex min-w-0 flex-wrap items-center gap-2 text-[11px] font-semibold text-neutral-500">
                                                                        <span className="inline-flex min-w-0 items-center gap-1 px-1 py-0.5">
                                                                            <MapPin size={12} className="text-rose-200/80" />
                                                                            <span className="truncate text-neutral-300">{session.ip || "Unknown IP"}</span>
                                                                        </span>
                                                                        <span className="inline-flex items-center gap-1 px-1 py-0.5">
                                                                            <Clock3 size={12} className="text-rose-200/75" />
                                                                            {formatSessionLoginTime(session.createdAt)}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => confirmLogoutSession(session)}
                                                                    disabled={revokingSessionId === session.id}
                                                                    className="grid h-9 w-9 shrink-0 place-items-center text-rose-100 transition hover:text-rose-50 hover:drop-shadow-[0_0_12px_rgba(244,63,94,0.55)] disabled:opacity-50"
                                                                    title={session.current ? "Log out this device" : "Log out this session"}
                                                                    aria-label={session.current ? "Log out this device" : "Log out this session"}
                                                                >
                                                                    {revokingSessionId === session.id ? <RefreshCw size={15} className="animate-spin" /> : <LogOut size={15} />}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        {!isPresence && (
                                            <div className="border-t border-white/[0.06]">
                                                <div className="border-b border-white/[0.06] px-4 py-3.5 last:border-b-0">
                                                    <div className="flex items-center justify-between gap-3">
                                                        <div className="flex min-w-0 items-center gap-2.5">
                                                            <span className="grid h-7 w-7 shrink-0 place-items-center text-neutral-500">
                                                                <RotateCcw size={15} />
                                                            </span>
                                                            <span className="min-w-0 truncate text-sm font-semibold text-neutral-100">
                                                                Reset Session
                                                            </span>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={resetLocalSession}
                                                            className={dangerButtonClass}
                                                        >
                                                            Reset
                                                        </button>
                                                    </div>
                                                    <p className="mt-1.5 pl-9 text-xs leading-5 text-neutral-500">
                                                        Sign out everywhere and reset this browser.
                                                    </p>
                                                </div>

                                                <div className="px-4 py-3.5">
                                                    <div className="flex items-center justify-between gap-3">
                                                        <div className="flex min-w-0 items-center gap-2.5">
                                                            <span className="grid h-7 w-7 shrink-0 place-items-center text-neutral-500">
                                                                <Trash2 size={15} />
                                                            </span>
                                                            <span className="min-w-0 truncate text-sm font-semibold text-neutral-100">
                                                                Delete Account
                                                            </span>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={deleteCurrentAccount}
                                                            className="shrink-0 rounded-xl bg-rose-600 px-3 py-2 text-xs font-black text-white transition hover:bg-rose-500"
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                    <p className="mt-1.5 pl-9 text-xs leading-5 text-neutral-500">
                                                        Permanent and cannot be undone.
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </Section>
                            )}

                        </>
                    )}
                </div>
            </main>

            {appearanceModal && (
                <div
                    className="fixed inset-0 z-[80] flex items-end justify-center bg-black/75 px-3 py-4 backdrop-blur-md sm:items-center"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) closeAppearanceModal();
                    }}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-label={appearanceModal === "themes" ? "Manage visible themes" : "Manage visible languages"}
                        className="w-full max-w-lg overflow-hidden rounded-[1.6rem] border border-cyan-300/15 bg-[#05070b]/95 shadow-[0_0_42px_rgba(0,0,0,0.65)]"
                    >
                        <div className="flex items-center justify-between gap-3 border-b border-white/[0.06] px-4 py-3">
                            <div>
                                <div className="text-base font-black text-white">
                                    {appearanceModal === "themes" ? "Manage Themes" : "Manage Languages"}
                                </div>
                                <p className="mt-1 text-xs leading-5 text-neutral-500">
                                    {appearanceModal === "themes"
                                        ? "Select a theme and choose which tiles stay visible."
                                        : "Choose which language codes stay visible."}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={closeAppearanceModal}
                                className="grid h-9 w-9 shrink-0 place-items-center text-neutral-400 transition hover:text-white"
                                aria-label="Close"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="max-h-[70vh] overflow-y-auto px-4 py-3">
                            {appearanceModal === "themes" ? (
                                <div className="space-y-3">
                                    {THEME_GROUPS.map((group) => (
                                        <div key={group.title}>
                                            <div className="mb-2 text-[10px] font-black uppercase tracking-[0.24em] text-neutral-500">
                                                {group.title}
                                            </div>
                                            <div className="grid gap-2 sm:grid-cols-2">
                                                {group.themes.map((themeName) => {
                                                    const theme = THEME_OPTIONS.find((option) => option.name === themeName);
                                                    if (!theme) return null;
                                                    const active = form.theme === theme.name;
                                                    const visible = visibleThemeNames.includes(theme.name);
                                                    return (
                                                        <div
                                                            key={theme.name}
                                                            className={classNames(
                                                                "flex items-center gap-2 rounded-2xl border p-2 transition",
                                                                active
                                                                    ? "border-cyan-200/45 bg-cyan-300/12 shadow-[0_0_30px_rgba(34,211,238,0.32),inset_0_0_18px_rgba(34,211,238,0.10)]"
                                                                    : visible
                                                                        ? "border-cyan-300/12 bg-cyan-300/[0.035] shadow-[0_0_18px_rgba(34,211,238,0.10)]"
                                                                        : "border-white/[0.04] bg-white/[0.018] opacity-75"
                                                            )}
                                                        >
                                                            <button
                                                                type="button"
                                                                onClick={() => selectTheme(theme.name)}
                                                                className="flex min-w-0 flex-1 items-center gap-3 text-left"
                                                            >
                                                                {renderThemeSwatches(theme, "grid h-9 w-10 shrink-0 place-content-center grid-cols-2 gap-0.5 rounded-xl bg-black/35 p-1.5")}
                                                                <span className="min-w-0">
                                                                    <span className="block truncate text-sm font-black text-neutral-100">
                                                                        {theme.name}
                                                                    </span>
                                                                    <span className="block truncate text-[11px] font-semibold text-neutral-500">
                                                                        {theme.bestFor}
                                                                    </span>
                                                                </span>
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => toggleVisibleTheme(theme.name)}
                                                                className={classNames(
                                                                    "shrink-0 rounded-xl border px-2.5 py-2 text-[10px] font-black uppercase tracking-[0.16em] transition",
                                                                    visible
                                                                        ? "border-cyan-200/35 bg-cyan-300/14 text-cyan-50 shadow-[0_0_16px_rgba(34,211,238,0.24)]"
                                                                        : "border-white/[0.05] bg-black/35 text-neutral-500 hover:text-white"
                                                                )}
                                                            >
                                                                {visible ? "Hide" : "Show"}
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                                    {LANGUAGE_OPTIONS.map((language) => {
                                        const active = form.language === language.code;
                                        const visible = visibleLanguageCodes.includes(language.code);
                                        return (
                                            <div
                                                key={language.code}
                                                className={classNames(
                                                    "rounded-2xl border p-2 transition",
                                                    active
                                                        ? "border-emerald-200/45 bg-emerald-300/12 shadow-[0_0_28px_rgba(110,231,183,0.28),inset_0_0_18px_rgba(110,231,183,0.10)]"
                                                        : visible
                                                            ? "border-emerald-300/12 bg-emerald-300/[0.035] shadow-[0_0_18px_rgba(110,231,183,0.10)]"
                                                            : "border-white/[0.04] bg-white/[0.018] opacity-75"
                                                )}
                                            >
                                                <div className="flex w-full items-center gap-3">
                                                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-black/35 text-sm font-black text-emerald-100">
                                                        {language.code}
                                                    </span>
                                                    <span className="min-w-0">
                                                        <span className="block truncate text-sm font-black text-neutral-100">
                                                            {language.label}
                                                        </span>
                                                        <span className="block text-[11px] font-semibold text-neutral-500">
                                                            {active ? "Active" : "Available"}
                                                        </span>
                                                    </span>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => selectLanguage(language.code)}
                                                    aria-pressed={active}
                                                    className={classNames(
                                                        "mt-2 w-full rounded-xl border px-2.5 py-2 text-[10px] font-black uppercase tracking-[0.16em] transition",
                                                        active
                                                            ? "border-emerald-200/35 bg-emerald-300/14 text-emerald-50"
                                                            : "border-white/[0.06] bg-black/35 text-neutral-200 hover:bg-white/[0.06]"
                                                    )}
                                                >
                                                    {active ? "Selected" : "Select"}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => toggleVisibleLanguage(language.code)}
                                                    className={classNames(
                                                        "mt-2 w-full rounded-xl border px-2.5 py-2 text-[10px] font-black uppercase tracking-[0.16em] transition",
                                                        visible
                                                            ? "border-emerald-200/35 bg-emerald-300/14 text-emerald-50 shadow-[0_0_16px_rgba(110,231,183,0.22)]"
                                                            : "border-white/[0.05] bg-black/35 text-neutral-500 hover:text-white"
                                                    )}
                                                >
                                                    {visible ? "Hide" : "Show"}
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div className="border-t border-white/[0.06] px-4 py-3">
                            <button
                                type="button"
                                onClick={saveAppearanceOnly}
                                disabled={saving}
                                className={classNames(
                                    "w-full rounded-xl px-4 py-3 text-sm font-black transition disabled:cursor-not-allowed disabled:opacity-60",
                                    appearanceDirty
                                        ? "bg-cyan-300/14 text-cyan-50 shadow-[0_0_22px_rgba(34,211,238,0.18)] hover:bg-cyan-300/20"
                                        : "bg-white/[0.04] text-white hover:bg-white/[0.07]"
                                )}
                            >
                                {saving ? "Saving..." : appearanceDirty ? "Save appearance" : "Done"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {twoFactorModalOpen && (
                <div
                    className="fixed inset-0 z-[90] flex items-end justify-center bg-black/75 px-3 py-4 backdrop-blur-md sm:items-center"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) closeTwoFactorModal();
                    }}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-label="2FA setup"
                        className="w-full max-w-md overflow-hidden rounded-[1.55rem] border border-emerald-300/15 bg-[#05070b]/95 shadow-[0_0_44px_rgba(0,0,0,0.7)]"
                    >
                        <div className="relative overflow-hidden border-b border-white/[0.06] px-4 py-4">
                            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_16%_0%,rgba(52,211,153,0.16),transparent_34%),linear-gradient(120deg,transparent,rgba(52,211,153,0.06),transparent)]" />
                            <div className="relative flex items-center justify-between gap-3">
                                <div className="flex min-w-0 items-center gap-3">
                                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[0.9rem] bg-emerald-300/10 text-emerald-100 shadow-[0_0_18px_rgba(52,211,153,0.12)]">
                                        <ScanQrCode size={18} />
                                    </span>
                                    <div className="min-w-0">
                                        <div className="text-lg font-black text-white">2FA</div>
                                        <p className="mt-1 text-xs leading-5 text-neutral-500">
                                            Authenticator setup preview.
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={closeTwoFactorModal}
                                    className="grid h-9 w-9 shrink-0 place-items-center text-neutral-500 transition hover:text-white"
                                    aria-label="Close 2FA setup"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        </div>

                        <div className="space-y-3 px-4 py-4">
                            <div className="rounded-[1.15rem] bg-emerald-950/[0.06] p-3.5 ring-1 ring-emerald-300/10">
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <div className="text-sm font-black text-neutral-100">Authenticator app</div>
                                        <p className="mt-1 text-xs leading-5 text-neutral-500">
                                            QR setup and code verification will attach here.
                                        </p>
                                    </div>
                                    <span className="rounded-xl bg-emerald-300/10 px-3 py-2 text-xs font-black text-emerald-100">
                                        {twoFactorConfigured ? "Saved" : "Ready"}
                                    </span>
                                </div>
                            </div>

                            <div className="rounded-[1.15rem] bg-white/[0.025] p-3.5 ring-1 ring-white/[0.06]">
                                <div className="text-sm font-black text-neutral-100">Backup codes</div>
                                <p className="mt-1 text-xs leading-5 text-neutral-500">
                                    Reserved for recovery codes once real verification is wired.
                                </p>
                            </div>

                            {twoFactorError && (
                                <p className="rounded-xl border border-rose-400/20 bg-rose-950/25 px-3 py-2 text-xs font-bold text-rose-100">
                                    {twoFactorError}
                                </p>
                            )}
                        </div>

                        <div className="flex flex-col gap-2 border-t border-white/[0.06] px-4 py-4 sm:flex-row">
                            {twoFactorConfigured && (
                                <button
                                    type="button"
                                    onClick={() => saveTwoFactorState(false)}
                                    disabled={twoFactorSaving}
                                    className="rounded-xl border border-neutral-800 bg-black px-4 py-3 text-sm font-black text-neutral-300 transition hover:bg-white/[0.04] disabled:opacity-60"
                                >
                                    Remove
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => saveTwoFactorState(true)}
                                disabled={twoFactorSaving}
                                className="flex-1 rounded-xl bg-emerald-300/15 px-4 py-3 text-sm font-black text-emerald-50 shadow-[0_0_20px_rgba(52,211,153,0.14)] transition hover:bg-emerald-300/20 disabled:opacity-60"
                            >
                                {twoFactorSaving ? "Saving..." : twoFactorConfigured ? "Save 2FA" : "Set up 2FA"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {recoveryModalOpen && (
                <div
                    className="fixed inset-0 z-[90] flex items-end justify-center bg-black/75 px-3 py-4 backdrop-blur-md sm:items-center"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) closeRecoveryModal();
                    }}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-label="Recovery phrase setup"
                        className="max-h-[calc(100dvh-1.5rem)] w-full max-w-2xl overflow-y-auto rounded-[1.55rem] border border-emerald-300/15 bg-[#05070b]/95 shadow-[0_0_44px_rgba(0,0,0,0.7)]"
                    >
                        <div className="relative overflow-hidden border-b border-white/[0.06] px-4 py-4">
                            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_85%_0%,rgba(52,211,153,0.13),transparent_34%),linear-gradient(135deg,transparent,rgba(255,255,255,0.04),transparent)]" />
                            <div className="relative flex items-center justify-between gap-3">
                                <div className="flex min-w-0 items-center gap-3">
                                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[0.9rem] bg-emerald-300/10 text-emerald-100 shadow-[0_0_18px_rgba(52,211,153,0.12)]">
                                        <LifeBuoy size={18} />
                                    </span>
                                    <div className="min-w-0">
                                        <div className="text-lg font-black text-white">
                                            {recoveryConfigured ? "Rotate recovery phrase" : "Set recovery phrase"}
                                        </div>
                                        <p className="mt-1 text-xs leading-5 text-neutral-500">
                                            This phrase unlocks password recovery and confirms password changes.
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={closeRecoveryModal}
                                    className="grid h-9 w-9 shrink-0 place-items-center text-neutral-500 transition hover:text-white"
                                    aria-label="Close recovery phrase setup"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        </div>

                        <div className="space-y-4 px-4 py-4">
                            <div className="rounded-[1.15rem] bg-black/45 p-3.5 ring-1 ring-white/[0.06] transition focus-within:ring-emerald-300/35">
                                <span className="mb-2 block text-[11px] font-black uppercase tracking-[0.14em] text-emerald-100">Current password</span>
                                <input
                                    type="password"
                                    value={recoveryCurrentPassword}
                                    onChange={(event) => {
                                        setRecoveryCurrentPassword(event.target.value);
                                        if (recoveryError) setRecoveryError("");
                                    }}
                                    placeholder="Verify this identity"
                                    autoComplete="current-password"
                                    className="w-full bg-transparent text-sm font-semibold text-white outline-none placeholder:text-neutral-600"
                                />
                            </div>

                            {recoveryConfigured && (
                                <div className="rounded-[1.15rem] bg-black/45 p-3.5 ring-1 ring-white/[0.06] transition focus-within:ring-emerald-300/35">
                                    <span className="mb-2 block text-[11px] font-black uppercase tracking-[0.14em] text-emerald-100">Current recovery phrase</span>
                                    <input
                                        type="password"
                                        value={recoveryPreviousPhrase}
                                        onChange={(event) => {
                                            setRecoveryPreviousPhrase(event.target.value);
                                            if (recoveryError) setRecoveryError("");
                                        }}
                                        placeholder="Enter all 12 current words"
                                        autoComplete="off"
                                        autoCapitalize="none"
                                        spellCheck={false}
                                        className="w-full bg-transparent text-sm font-semibold text-white outline-none placeholder:text-neutral-600"
                                    />
                                </div>
                            )}

                            {recoveryGeneratedPhrase ? (
                                <RecoveryPhraseSetup
                                    key={recoveryGeneratedPhrase}
                                    phrase={recoveryGeneratedPhrase}
                                    generating={recoveryGenerating}
                                    disabled={recoverySaving}
                                    onConfirmedChange={setRecoveryPhraseConfirmed}
                                    onRegenerate={() => void loadNewRecoveryPhrase()}
                                />
                            ) : (
                                <div className="flex min-h-32 flex-col items-center justify-center gap-3 rounded-xl border border-white/[0.06] bg-black/35 px-4 text-center text-sm font-bold text-neutral-500">
                                    <span>{recoveryGenerating ? "Generating secure phrase..." : "Phrase unavailable."}</span>
                                    {!recoveryGenerating && (
                                        <button type="button" onClick={() => void loadNewRecoveryPhrase()} className={tinyButtonClass}>
                                            Try again
                                        </button>
                                    )}
                                </div>
                            )}

                            {recoveryError && (
                                <p className="rounded-xl border border-rose-400/20 bg-rose-950/25 px-3 py-2 text-xs font-bold text-rose-100">
                                    {recoveryError}
                                </p>
                            )}
                        </div>

                        <div className="flex flex-col gap-2 border-t border-white/[0.06] px-4 py-4 sm:flex-row">
                            <button
                                type="button"
                                onClick={closeRecoveryModal}
                                disabled={recoverySaving}
                                className="rounded-xl border border-neutral-800 bg-black px-4 py-3 text-sm font-black text-neutral-300 transition hover:bg-white/[0.04] disabled:opacity-60"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={saveRecoveryPhrase}
                                disabled={
                                    recoverySaving
                                    || recoveryGenerating
                                    || !recoveryPhraseConfirmed
                                    || !recoveryCurrentPassword
                                    || (recoveryConfigured && !recoveryPreviousPhrase)
                                }
                                className="flex-1 rounded-xl bg-emerald-300/15 px-4 py-3 text-sm font-black text-emerald-50 shadow-[0_0_20px_rgba(52,211,153,0.14)] transition hover:bg-emerald-300/20 disabled:opacity-60"
                            >
                                {recoverySaving ? "Securing..." : recoveryConfigured ? "Rotate phrase" : "Secure phrase"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <LogoutModal
                isOpen={showLogoutModal}
                onConfirm={handleLogout}
                onCancel={() => setShowLogoutModal(false)}
            />

            {isDirty && (
                <DirtySettingsBar
                    saving={saving}
                    disabled={saveDisabled}
                    onSave={() => onSave()}
                    onReset={onReset}
                />
            )}

            <ConfirmDialog
                confirm={confirmState}
                busy={confirmBusy}
                typed={confirmText}
                password={confirmPassword}
                onTyped={setConfirmText}
                onPassword={(value) => {
                    confirmPasswordRef.current = value;
                    setConfirmPassword(value);
                }}
                onCancel={closeConfirm}
                onConfirm={runConfirm}
            />

            <PasswordChangeDialog
                open={passwordDialogOpen}
                value={passwordForm}
                saving={passwordSaving}
                error={passwordError}
                onChange={(next) => {
                    setPasswordForm(next);
                    if (passwordError) setPasswordError("");
                }}
                onClose={closePasswordDialog}
                onSubmit={submitPasswordChange}
            />

            <FooterNav />

        </div>
    );
}

function normalizeLanguageCode(value: unknown): LanguageCode {
    const raw = String(value || "").trim();
    const upper = raw.toUpperCase();
    if (upper === "ENGLISH") return "EN";
    if (upper === "HINDI") return "HI";
    if (upper === "FRENCH") return "FR";
    if (upper === "ITALIAN") return "IT";
    if (upper === "RUSSIAN") return "RU";
    if (upper === "ARABIC") return "AR";
    if (["EN", "HI", "FR", "IT", "RU", "ZA", "AR"].includes(upper)) return upper as LanguageCode;
    return "EN";
}

function uniqueList<T extends string>(values: readonly T[]) {
    return Array.from(new Set(values));
}

function normalizeThemeList(value: unknown, fallback = DEFAULT_VISIBLE_THEMES): AppearanceTheme[] {
    const raw = Array.isArray(value) ? value : fallback;
    const valid = raw
        .map((item) => normalizeThemeName(String(item || "")))
        .filter((theme) => THEME_OPTIONS.some((option) => option.name === theme));
    const fallbackThemes = fallback
        .map((theme) => normalizeThemeName(String(theme || "")))
        .filter((theme) => THEME_OPTIONS.some((option) => option.name === theme));
    return uniqueList(valid.length > 0 ? valid : fallbackThemes).slice(0, THEME_OPTIONS.length);
}

function normalizeLanguageList(value: unknown, fallback = DEFAULT_VISIBLE_LANGUAGES): LanguageCode[] {
    const raw = Array.isArray(value) ? value : fallback;
    const valid = raw
        .map((item) => normalizeLanguageCode(item))
        .filter((code) => LANGUAGE_OPTIONS.some((option) => option.code === code));
    const fallbackLanguages = fallback
        .map((code) => normalizeLanguageCode(code))
        .filter((code) => LANGUAGE_OPTIONS.some((option) => option.code === code));
    return uniqueList(valid.length > 0 ? valid : fallbackLanguages).slice(0, LANGUAGE_OPTIONS.length);
}

function hydrateFormState(value?: Partial<FormState> | null): FormState {
    const next = { ...initialForm, ...(value || {}) } as FormState;
    next.theme = normalizeThemeName(String(next.theme || initialForm.theme));
    next.language = normalizeLanguageCode(next.language);
    next.visibleThemes = normalizeThemeList((value as any)?.visibleThemes, DEFAULT_VISIBLE_THEMES);
    next.visibleLanguages = normalizeLanguageList((value as any)?.visibleLanguages, DEFAULT_VISIBLE_LANGUAGES);
    return next;
}

function canonicalFormState(value?: Partial<FormState> | null): FormState {
    const next = hydrateFormState(value);
    return {
        ...next,
        avatarFile: null,
        visibleThemes: normalizeThemeList(next.visibleThemes, DEFAULT_VISIBLE_THEMES),
        visibleLanguages: normalizeLanguageList(next.visibleLanguages, DEFAULT_VISIBLE_LANGUAGES),
    };
}
